<template>
  <div ref="canvasContainer" class="canvas-container"
    :class="{ 'bubble-zoom-in': bubbleTransition === 'in', 'bubble-zoom-out': bubbleTransition === 'out', 'bubble-instant': bubbleNoTransition }"
    :style="{ '--zoom-origin': bubbleZoomOrigin }">
    <div v-if="isLoading" class="model-loading">
      <!-- Phase 1: downloading the model from the network (skipped when served from cache) -->
      <div v-if="loadingPhase === 'download'" class="ml-inner">
        <v-progress-circular :model-value="downloadPercent ?? 0" :indeterminate="downloadPercent == null"
          :size="96" :width="8" color="#66c7b4">
          <span v-if="downloadPercent != null" class="ml-percent">{{ downloadPercent }}%</span>
        </v-progress-circular>
        <div class="ml-title">Downloading model</div>
        <div class="ml-sub">Fetching the 3D scan…</div>
      </div>

      <!-- Phase 2: parsing + building the scene (blocks briefly on large models) -->
      <div v-else class="ml-inner">
        <div class="ml-cube">
          <span class="ml-face ml-front"></span>
          <span class="ml-face ml-back"></span>
          <span class="ml-face ml-right"></span>
          <span class="ml-face ml-left"></span>
          <span class="ml-face ml-top"></span>
          <span class="ml-face ml-bottom"></span>
        </div>
        <div class="ml-title">Preparing model</div>
        <div class="ml-sub">Loading into the viewer…</div>
      </div>
    </div>

    <!-- Single bottom toolbar: bubble label · prev/next capture · (thumbnail-capture controls, only when
         we entered to capture an image for a marker/hotspot) · exit. Browsing street view keeps it clean. -->
    <div v-if="bubbleActive && showBubbleBar" class="bubble-bar">
      <span class="bubble-label"><v-icon size="16">mdi-image-filter-center-focus</v-icon><span class="bl-text">Street view</span></span>

      <div v-if="bubbleCaptureCount > 1" class="bubble-steps">
        <button type="button" class="bubble-exit bubble-step" title="Previous capture"
          :disabled="bubbleCaptureIndex <= 0" @click="stepBubble(-1)">
          <v-icon size="18">mdi-chevron-left</v-icon>
        </button>
        <v-menu location="top" :close-on-content-click="true">
          <template #activator="{ props }">
            <button type="button" class="bubble-exit bubble-step bubble-counter" v-bind="props" title="Jump to a capture">
              <span>{{ bubbleCaptureIndex + 1 }} / {{ bubbleCaptureCount }}</span>
              <v-icon size="14">mdi-menu-down</v-icon>
            </button>
          </template>
          <v-list theme="dark" density="compact" class="bubble-jump-list">
            <v-list-item v-for="c in bubbleCaptureInfo" :key="c.index" :active="c.index === bubbleCaptureIndex"
              @click="jumpToCapture(c.index)">
              <v-list-item-title>{{ c.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <button type="button" class="bubble-exit bubble-step" title="Next capture"
          :disabled="bubbleCaptureIndex >= bubbleCaptureCount - 1" @click="stepBubble(1)">
          <v-icon size="18">mdi-chevron-right</v-icon>
        </button>
      </div>

      <!-- Draw the box to capture this marker/hotspot's image. Only in capture mode (re-capture, or just
           after creating one) — never while simply looking around. Capture mode is already permission-
           gated upstream (only someone allowed to (re)capture can enter it). -->
      <template v-if="bubbleCaptureMode">
        <span class="bubble-bar-sep"></span>
        <button v-if="!bubbleDrawActive" type="button" class="bubble-exit bubble-mark" @click="startBubbleDraw">
          <v-icon size="16">mdi-vector-polygon</v-icon><span>Create thumbnail</span>
        </button>
        <template v-else>
          <span class="bubble-draw-hint">{{ bubbleDrawPoints.length < 3 ? 'Outline the finding' : 'Drag a point to adjust, or add more' }}</span>
          <button type="button" class="bubble-exit bubble-create" :disabled="bubbleDrawPoints.length < 3" @click="createFromBubblePolygon">
            <v-icon size="16">mdi-check</v-icon><span>Save</span>
          </button>
          <button type="button" class="bubble-exit" :disabled="!bubbleDrawPoints.length" @click="clearBubbleDraw">
            <v-icon size="16">mdi-backup-restore</v-icon><span>Clear</span>
          </button>
          <button type="button" class="bubble-exit" @click="cancelBubbleDraw">
            <v-icon size="16">mdi-close</v-icon><span>Cancel</span>
          </button>
        </template>
        <span class="bubble-bar-sep"></span>
      </template>

      <button type="button" class="bubble-exit" @click="exitBubbleWithTransition">
        <v-icon size="16">mdi-close</v-icon><span>Exit</span>
      </button>
    </div>

    <!-- Single-lens capture: the frame shown FLAT (pan/zoom), covering the 3D scene. Drawing a box on it
         is a direct pixel crop (no 3D). Sits above the canvas, below the draw layer + toolbar. -->
    <div v-if="bubbleActive && bubbleFlatUrl" class="bubble-flat" :class="{ dragging: flatDragging, 'is-zoomed': flatZoom > 1 }"
      @wheel.prevent.stop="onFlatWheel" @mousedown="onFlatDragStart" @mousemove="onFlatDragMove"
      @mouseup="onFlatDragEnd" @mouseleave="onFlatDragEnd">
      <img ref="bubbleFlatImg" :src="bubbleFlatUrl" class="bubble-flat-img" :style="flatStyle" alt="Capture"
        crossorigin="anonymous" draggable="false" />
    </div>

    <!-- Polygon drawing surface over the WebGL canvas (only while the tool is armed). Captures clicks
         so they don't reach OrbitControls; look-around is frozen while drawing. -->
    <div v-if="bubbleActive && bubbleDrawActive" class="bubble-draw-layer"
      @click="onBubbleDrawClick" @contextmenu.prevent="undoBubbleDrawPoint"
      @pointermove="onVertexDragMove" @pointerup="endVertexDrag" @pointerleave="endVertexDrag">
      <svg class="bubble-draw-svg">
        <polygon v-if="bubbleDrawPoints.length > 2" :points="bubbleDrawPointsAttr" class="bd-poly" />
        <polyline v-else-if="bubbleDrawPoints.length > 1" :points="bubbleDrawPointsAttr" class="bd-line" />
        <circle v-for="(p, i) in bubbleDrawPoints" :key="i" :cx="p.x" :cy="p.y" r="6" class="bd-vert"
          :class="{ dragging: draggingVertex === i }" @pointerdown.stop="startVertexDrag(i, $event)" />
      </svg>
    </div>

    <!-- Street-View-style pegman: drag onto a walking path to drop into that photo bubble. -->
    <div v-if="pegmanVisible && showPegman" class="pegman" :class="{ dragging: pegDragging }"
      title="Drag onto a walking path to look around" @pointerdown="onPegPointerDown">
      <!-- eslint-disable-next-line vue/no-v-html -->
      <svg class="pegman-fig" viewBox="0 0 44 68" width="40" height="62" v-html="pegmanSvg"></svg>
    </div>
    <div v-if="pegDragging" class="peg-ghost" :class="{ snap: pegHasCandidate }"
      :style="{ left: pegCursor.x + 'px', top: pegCursor.y + 'px' }">
      <span class="peg-glow"></span>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <svg class="pegman-fig" viewBox="0 0 44 68" width="46" height="71" v-html="pegmanSvg"></svg>
    </div>

  </div>
</template>

<style scoped>
/* The single bubble toolbar, pinned bottom-centre. Matches the viewer tools pill (dark card, blur). */
.bubble-bar {
  position: absolute; bottom: 16px; left: 50%; transform: translateX(-50%); z-index: 20;
  display: flex; align-items: center; gap: 10px; max-width: calc(100% - 24px);
  height: clamp(46px, calc(34px + 0.9vw), 52px); padding: 0 10px 0 14px;
  background: rgba(20, 30, 36, 0.82); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.35); backdrop-filter: blur(6px);
  color: #e3eef0; font-size: 0.85rem; font-weight: 600;
}
.bubble-bar-sep { width: 1px; align-self: stretch; margin: 8px 2px; background: rgba(255, 255, 255, 0.12); }
.bubble-label { display: inline-flex; align-items: center; gap: 6px; white-space: nowrap; }
/* Matches the tools pill's .tb-mode buttons. */
.bubble-exit {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; gap: 6px;
  height: clamp(32px, calc(24px + 0.6vw), 36px); padding: 0 14px; border-radius: 9px;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.08); color: #e3eef0;
  font: inherit; font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.bubble-exit:hover:not(:disabled) { background: rgba(255, 255, 255, 0.09); color: #fff; }
.bubble-exit:disabled { opacity: 0.45; cursor: not-allowed; }
.bubble-mark.bubble-exit { color: var(--duallo-accent); border-color: rgba(100, 255, 218, 0.3); }
.bubble-draw-hint { font-size: 0.78rem; color: #9fb2bb; white-space: nowrap; }

/* Prev / next capture arrows — step along the walk without leaving the bubble. */
.bubble-steps { display: inline-flex; align-items: center; gap: 4px; }
.bubble-step.bubble-exit { padding: 0 8px; }
.bubble-counter { display: inline-flex; align-items: center; gap: 2px; min-width: 64px; justify-content: center; font-variant-numeric: tabular-nums; font-size: 0.82rem; }
.bubble-jump-list { max-height: 260px; overflow-y: auto; background: #10171d; }

.bubble-create.bubble-exit { color: var(--duallo-accent); border-color: rgba(100, 255, 218, 0.35); }
.bubble-create.bubble-exit:hover:not(:disabled) { background: rgba(100, 255, 218, 0.12); }

/* Single-lens capture shown flat (2D), above the canvas, below the draw layer + toolbar. */
.bubble-flat {
  position: absolute; inset: 0; z-index: 10; overflow: hidden;
  display: flex; align-items: center; justify-content: center; background: #0a0f12;
}
.bubble-flat.is-zoomed { cursor: grab; }
.bubble-flat.dragging { cursor: grabbing; }
/* No object-fit: with max-width/height the element box IS the rendered image, so screen→pixel mapping
   stays exact (no letterbox offset) and the transformed getBoundingClientRect is the image rect. */
.bubble-flat-img {
  max-width: 100%; max-height: 100%; display: block;
  transform-origin: center center; user-select: none; -webkit-user-drag: none; will-change: transform;
}

/* Polygon drawing surface — above the canvas, below the bubble-bar. */
.bubble-draw-layer { position: absolute; inset: 0; z-index: 15; cursor: crosshair; }
.bubble-draw-svg { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; }
.bd-poly { fill: rgba(100, 255, 218, 0.14); stroke: var(--duallo-accent); stroke-width: 2; }
.bd-line { fill: none; stroke: var(--duallo-accent); stroke-width: 2; stroke-dasharray: 5 4; }
.bd-vert { fill: #0f171b; stroke: var(--duallo-accent); stroke-width: 2; pointer-events: all; cursor: grab; }
.bd-vert:hover { fill: var(--duallo-accent); }
.bd-vert.dragging { cursor: grabbing; fill: var(--duallo-accent); }

/* Tight widths (higher zoom): keep the pill compact — icon-only Exit, hide the label. */
@media (max-width: 1440px) {
  .bubble-label .bl-text { display: none; }
}

/* Street-View-style zoom + blur transition on the rendered canvas (deep — canvas is added by three).
   Enter zooms IN toward the drop point; exit zooms OUT. The reveal snaps (bubble-instant kills the
   transition) so the bubble / point cloud simply appears with no settle afterwards. */
.canvas-container :deep(canvas) { transition: filter 0.55s ease, transform 0.55s ease; will-change: filter, transform; }
.canvas-container.bubble-instant :deep(canvas) { transition: none; }
.canvas-container.bubble-zoom-in :deep(canvas) {
  filter: blur(13px) saturate(1.06); transform: scale(1.3); transform-origin: var(--zoom-origin, center);
}
.canvas-container.bubble-zoom-out :deep(canvas) {
  filter: blur(13px) saturate(1.06); transform: scale(0.82); transform-origin: center;
}

.pegman {
  position: absolute; left: 50%; bottom: 12px; transform: translateX(-50%); z-index: 20;
  display: flex; align-items: flex-end; justify-content: center;
  cursor: grab; touch-action: none; user-select: none;
}
/* Ground shadow so the figure reads as standing in the scene. */
.pegman::after {
  content: ''; position: absolute; bottom: -2px; left: 50%; transform: translateX(-50%);
  width: 22px; height: 6px; border-radius: 50%; background: rgba(0, 0, 0, 0.4); filter: blur(1px);
}
.pegman-fig {
  display: block; filter: drop-shadow(0 3px 3px rgba(0, 0, 0, 0.45));
  transform-origin: bottom center; transition: transform 0.15s ease;
}
/* Google-Maps-style: the figure does a little hop/sway on hover. */
.pegman:hover .pegman-fig { animation: peg-hop 0.6s ease-in-out infinite; }
@keyframes peg-hop {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25% { transform: translateY(-3px) rotate(-5deg); }
  75% { transform: translateY(-3px) rotate(5deg); }
}
@media (prefers-reduced-motion: reduce) { .pegman:hover .pegman-fig { animation: none; } }
.pegman.dragging { opacity: 0.35; cursor: grabbing; }

.peg-ghost {
  position: fixed; z-index: 60; transform: translate(-50%, -88%); pointer-events: none;
  display: flex; align-items: flex-end; justify-content: center;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.55));
}
/* Glowy circle under the figure while dragging (teal when snapping to a path, amber otherwise). */
.peg-glow {
  position: absolute; left: 50%; bottom: -4px; transform: translateX(-50%);
  width: 36px; height: 13px; border-radius: 50%;
  background: radial-gradient(closest-side, rgba(249, 176, 0, 0.85), rgba(249, 176, 0, 0));
  animation: peg-glow-pulse 1s ease-in-out infinite;
}
.peg-ghost.snap .peg-glow {
  background: radial-gradient(closest-side, rgba(100, 255, 218, 0.95), rgba(100, 255, 218, 0));
}
@keyframes peg-glow-pulse {
  0%, 100% { opacity: 0.55; transform: translateX(-50%) scale(0.9); }
  50% { opacity: 1; transform: translateX(-50%) scale(1.12); }
}
</style>

<script>
import { markRaw } from 'vue';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import ModelLoader from '@/utils/ModelLoader';
import {
  computeBoundsTree, disposeBoundsTree,
  computeBatchedBoundsTree, disposeBatchedBoundsTree, acceleratedRaycast, INTERSECTED, NOT_INTERSECTED
} from 'three-mesh-bvh';
import HotSpotMarker from '@/domain/marker/HotSpotMarker';
import CameraHelperMarker from '@/domain/marker/CameraHelperMarker';
import InspectionAreaMarker from '@/domain/marker/InspectionAreaMarker';
import AnomalyMarker from '@/domain/marker/AnomalyMarker';
import CameraPosition from '@/domain/modelversion/CameraPosition';
import * as markerGeometry from '@/lib/markerGeometry';
import { useViewerUiStore } from '@/store/viewerUiStore';
import { useOrganizationStore } from '@/store/organizationStore';
import AssetService from '@/services/AssetService';
import ModelVersionService from '@/services/ModelVersionService';
import DualloFramesClient from '@/clients/duallo/DualloFramesClient';
import organizationSasService from '@/services/OrganizationSasService';

// Filename without directory or extension, lowercased — pairs a capture point (helper image name)
// to its stored frame image regardless of extension.
const stemOf = (fileName) =>
  (fileName ?? '').split(/[\\/]/).pop().replace(/\.[^.]+$/, '').toLowerCase();

// Normalise a capture stem to a modality-agnostic key by stripping a trailing modality tag, so a
// cube base and an equirect name for the same capture collapse to the SAME key:
//   "frame_00052_cube"     → "frame_00052"   (cube base, after its face suffix was removed)
//   "frame_00052_equirect" → "frame_00052"   (equirect blob stem)
const stripModality = (stem) => (stem || '').replace(/[_-](equirect|cube|pano|360)$/i, '');

// Capture key for an equirect blob path, matching the (normalised) cube base.
const equirectBase = (path) => stripModality(stemOf(path));

// Radial lens-undistort strength for the street-view cube faces (k1). Default 0 = NO warp (identical to a
// plain crop), so captures that already look right are untouched. Negative corrects barrel/fisheye; tune
// live with window.setCubeK(k), then re-enter the bubble, and only lock in a non-zero value if it helps.
const DEFAULT_CUBE_UNDISTORT_K = 0;

// Cube-face suffix → canonical face name. Supports the front/back/left/right/up/down naming and the
// OpenGL px/nx/py/ny/pz/nz axes (pz==front to match dedupeCubemapHelpers).
const CUBE_FACE_ALIAS = {
  front: 'front', pz: 'front', back: 'back', nz: 'back',
  right: 'right', px: 'right', left: 'left', nx: 'left',
  up: 'up', top: 'up', py: 'up', down: 'down', bottom: 'down', ny: 'down',
};

// Canonical cube stitched from the 6 named faces — NOT from per-camera world poses. The bubble hides
// the point cloud and markers, so the cube's yaw is arbitrary; what must be right is up-on-top,
// down-on-bottom and the four sides tiling in a consistent ring. Each face plane sits at `dir`*R from
// the centre facing inward; `up` is the in-plane texture-up reference. Tuning knobs during testing:
//   • wrong side (e.g. a face shows where another should) → swap `dir` entries
//   • face rotated 90°/180° → change `up`
//   • face mirrored → set `mirror: true`
const CUBE_FACE_LAYOUT = {
  front: { dir: [0, 1, 0], up: [0, 0, 1] },
  back: { dir: [0, -1, 0], up: [0, 0, 1] },
  right: { dir: [1, 0, 0], up: [0, 0, 1] },
  left: { dir: [-1, 0, 0], up: [0, 0, 1] },
  up: { dir: [0, 0, 1], up: [0, -1, 0] },   // top face reads 180° out with [0,1,0]
  down: { dir: [0, 0, -1], up: [0, 1, 0] },
};

// Suffix (from CUBE_FACE_RE group 2) → canonical face, for stitching by name.
const cubeFaceName = (imageName) => {
  const m = (imageName ?? '').match(/[_-](front|back|left|right|up|down|top|bottom|px|nx|py|ny|pz|nz)(\.[^.]+)?$/i);
  return m ? CUBE_FACE_ALIAS[m[1].toLowerCase()] : null;
};
import { computeScanPerimeter } from '@/lib/viewer/scanPerimeter';
import { faceLabelForWorldDir, projectWorldToFace, projectWorldToFaceDir, equirectBoxFromWorldPoints } from '@/lib/equirectProjection';
import {
  installCullShader,
  setMaterialTrimRadius,
  setMaterialClipBox,
  setMaterialSliceBox,
  attachCullDistances,
  createCameraPath,
  decimatePathCenters,
  dedupeCubemapHelpers,
  helperPositions,
  CAMERA_PATH_COLORS,
} from '@/lib/viewer/pointCloudCull';


export default {
  name: 'ModelViewer',
  setup() {
    return { viewerUi: useViewerUiStore(), organizationStore: useOrganizationStore() };
  },
  props: {
    assetId: {
      type: String,
      required: true,
    },
    modelVersion: {
      type: Object,
      default: null,
    },
    markers: {
      type: Array,
      default: () => []
    },
    // The built-in floating pegman is hidden when the parent hosts its own Street-view control (the
    // viewer tool rail) and drives the drag via startPegDrag(). Defaults true to preserve standalone use.
    showPegman: {
      type: Boolean,
      default: true,
    },
    // The in-viewer "Bubble view" exit bar. Kept separate from showPegman so a parent can host its own
    // street-view launcher (showPegman:false) yet still rely on this exit where its own chrome is hidden
    // (e.g. the mobile bubble view). Defaults true for standalone use.
    showBubbleBar: {
      type: Boolean,
      default: true,
    },
    // The cursor-following placement sphere is a precise-pointer affordance for creating markers.
    // Parents disable it on touch (where marker creation is off and there is no hover cursor).
    hoverPlacement: {
      type: Boolean,
      default: true,
    },
    // Enables the in-bubble polygon "mark area" annotation tool (desktop + AnomalyCreate permission).
    allowAnnotate: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['markerSelected', 'markerModified', 'insideInspectionArea', 'onPointSelected', 'isTransformActive', 'onModelLoaded', 'onPositionChanged', 'onAreaConflict', 'mobileMarkerSelected', 'mobileHotSpotSelected', 'onPlaybackState', 'onStreetViewAvailable', 'onBubbleState', 'onBubbleAnnotation', 'onBubbleAnnotationError', 'onBubbleReimage'],
  data() {
    return {
      clickTimeout: null,
      touchTap: null,   // in-progress single-finger touch: { x, y, moved } — a clean tap selects a marker

      selectedMarker: null,
      selectedPoint: null,
      modelLoader: null,

      isTransformActive: false,
      pointCloud: null,
      selectedMarkerGroupTransformBackup: null,
      highlightedPoint: new THREE.Vector3(),
      highlightedPointIndex: null,
      selectedPointIndex: null,

      selectedInspectionAreaMarker: null,
      selectedInspectionAreaMarkerGroupTransformBackup: null,

      // The inspection-area marker currently selected (by clicking its edge/label), and the zone
      // the point cloud is being isolated to (when the JIT "Isolate" toggle is on).
      selectedInspectionArea: null,
      isolatedZone: null,

      // World-space bounding box of the loaded model ({ min:{x,y,z}, max:{x,y,z} }), set by
      // fitClipPlanesToModel after load. Drives the slice-tool slider ranges.
      worldBounds: null,

      isLoading: true,
      // 'processing' by default so cache-hit loads show the spinner immediately; flips to
      // 'download' if a network fetch starts. downloadProgress is 0..1 (or null = indeterminate).
      loadingPhase: 'processing',
      downloadProgress: null,

      resizeTimeout: null,

      currentSphereCollisionObject: null,

      markerdetailswindowopen: false,

      markerGroupContainer: null,
      renderedMarkers: new Map(),

      // scanId of the scan currently under the hover cursor (set while picking). Used to place
      // new annotations against the right scan in that scan's local coordinates.
      highlightedScanId: null,

      // Walkthrough playback — fly the camera along a scan's capture path. Reactive UI state only; the
      // spline + arc-lengths live on plain instance fields (see buildPlaybackPath).
      playbackAvailable: false,
      isPlaying: false,
      isPaused: false,
      playbackProgress: 0,   // 0..1 along the path
      playbackSpeed: 1,      // multiplier over the base traversal speed
      bubbleActive: false,   // inside-the-photo bubble view at a capture point
      // Single-lens (mono) capture is shown as a FLAT 2D image (no 3D), so drawing a box is a direct
      // pixel crop. null while in a 360/cube/equirect bubble.
      bubbleFlatUrl: null,   // signed URL of the mono frame
      bubbleFlatName: null,  // its filename (stored as the marker's source image)
      flatZoom: 1, flatPanX: 0, flatPanY: 0, flatDragging: false, flatDragLastX: 0, flatDragLastY: 0,
      bubbleCaptureMode: false, // entered to capture a marker/hotspot image (re-capture or post-create) →
                                // the "Create thumbnail" draw controls show; false while just browsing
      bubbleCaptureCount: 0, // captures on this scan's walk (reactive mirror of _bubbleCaptures.length)
      bubbleCaptureIndex: -1,// position along that walk (for the prev/next capture arrows)
      bubbleCaptureInfo: [], // [{ index, label }] for the quick-hop jump menu between the arrows
      bubbleDrawActive: false,   // in-bubble polygon annotation tool is armed (look-around frozen)
      bubbleDrawPoints: [],      // polygon vertices in canvas CSS px: [{ x, y }]
      draggingVertex: null,      // index of the polygon vertex currently being dragged (or null)
      capturePointsAvailable: false, // any scan has capture points → show the pegman
      pegDragging: false,    // pegman being dragged onto a path
      pegHasCandidate: false,// currently hovering a snappable capture point
      pegCursor: { x: 0, y: 0 },
      bubbleTransition: '',  // '' | 'in' | 'out' — drives the zoom+blur transition
      bubbleNoTransition: false, // disables the CSS transition so the reveal snaps (no settle)
      bubbleZoomOrigin: 'center', // CSS transform-origin for the zoom (the drop point)
      // Modern flat PPE figure (hi-vis vest + hard hat), shared by the resting pegman and the drag
      // ghost — the Street-View-style control for dropping into a photo bubble.
      pegmanSvg: `
        <ellipse cx="22" cy="16.6" rx="12.6" ry="3.1" fill="#e9a417"/>
        <path d="M13.2 16.6 a8.8 8.8 0 0 1 17.6 0 z" fill="#ffc62e"/>
        <path d="M15 16.6 a7 7 0 0 1 7 -6.8 l0 6.8 z" fill="#ffdc6b"/>
        <rect x="21" y="9.6" width="2" height="7" rx="1" fill="#e9a417"/>
        <circle cx="22" cy="22.4" r="5.2" fill="#eab98d"/>
        <path d="M17 23.2 a5.2 5.2 0 0 0 10 0 z" fill="#d9a578" opacity="0.5"/>
        <rect x="19.5" y="26" width="5" height="3" rx="1.5" fill="#d9a578"/>
        <rect x="13.6" y="27.6" width="16.8" height="21" rx="6.5" fill="#2c313c"/>
        <rect x="12.9" y="28.5" width="18.2" height="19.4" rx="6.2" fill="#ddf23a"/>
        <path d="M19 28.7 l3 3 l3 -3 z" fill="#2c313c"/>
        <rect x="21.6" y="31" width="0.8" height="16.6" fill="#aeb90f"/>
        <rect x="11.8" y="34.4" width="20.4" height="2.6" rx="1.1" fill="#d7e1e8"/>
        <rect x="11.8" y="40.2" width="20.4" height="2.6" rx="1.1" fill="#d7e1e8"/>
        <rect x="8.7" y="29" width="5" height="15.2" rx="2.5" fill="#2c313c"/>
        <rect x="30.3" y="29" width="5" height="15.2" rx="2.5" fill="#2c313c"/>
        <rect x="8.7" y="40.6" width="5" height="3.1" rx="1.4" fill="#ddf23a"/>
        <rect x="30.3" y="40.6" width="5" height="3.1" rx="1.4" fill="#ddf23a"/>
        <circle cx="11.2" cy="45.4" r="2.5" fill="#23272f"/>
        <circle cx="32.8" cy="45.4" r="2.5" fill="#23272f"/>
        <rect x="15.4" y="47.6" width="5.6" height="15.6" rx="2.3" fill="#3a4150"/>
        <rect x="23" y="47.6" width="5.6" height="15.6" rx="2.3" fill="#3a4150"/>
        <rect x="13.4" y="60" width="8" height="4.7" rx="2.3" fill="#17191f"/>
        <rect x="22.6" y="60" width="8" height="4.7" rx="2.3" fill="#17191f"/>
        <rect x="13.4" y="63.2" width="8" height="1.5" rx="0.7" fill="#454b55"/>
        <rect x="22.6" y="63.2" width="8" height="1.5" rx="0.7" fill="#454b55"/>
      `,
      // Scans that have a walkable path, and which one is selected (multi-scan models let the user pick).
      playbackScans: [],     // [{ scanId, name }]
      selectedPlaybackScanId: null,
    }
  },
  computed: {
    downloadPercent() {
      return this.downloadProgress == null ? null : Math.round(this.downloadProgress * 100);
    },
    // Pan/zoom transform for the flat (mono) capture image.
    flatStyle() {
      return { transform: `translate(${this.flatPanX}px, ${this.flatPanY}px) scale(${this.flatZoom})` };
    },
    // Pegman (Street-View bubble) is a pointer-drag interaction — keep it on tablet + desktop, but
    // hide it on phones (xs) where the drag is too fiddly and the screen too small.
    pegmanVisible() {
      return this.capturePointsAvailable && !this.bubbleActive && !this.$vuetify?.display?.xs;
    },
    // SVG points="x,y x,y ..." for the in-bubble annotation polygon.
    bubbleDrawPointsAttr() {
      return this.bubbleDrawPoints.map(p => `${p.x},${p.y}`).join(' ');
    },
  },
  watch: {
    markers: {
      immediate: true,
      deep: true,
      handler() {
        if (!this.scene) return;
        this.syncMarkerGroups();
      }
    },
    // Let the parent's tool rail enable/disable its Street-view control in step with capture points.
    capturePointsAvailable: {
      immediate: true,
      handler(v) { this.$emit('onStreetViewAvailable', !!v); }
    },
    // Surface the full bubble (street-view) state so the PARENT can host the toolbar (exit, capture
    // arrows, "Create thumbnail" draw controls). The child just does the drawing; the parent owns the UI.
    bubbleActive() { this.emitBubbleState(); },
    bubbleCaptureMode() { this.emitBubbleState(); },
    bubbleDrawActive() { this.emitBubbleState(); },
    bubbleCaptureCount() { this.emitBubbleState(); },
    bubbleCaptureIndex() { this.emitBubbleState(); },
    'bubbleDrawPoints.length'() { this.emitBubbleState(); },
  },
  async mounted() {
    // TEMP tuning hook for the street-view cube lens-undistort: window.setCubeK(k) then re-enter the
    // bubble. Remove once DEFAULT_CUBE_UNDISTORT_K is locked in.
    if (typeof window !== 'undefined') {
      window.setCubeK = (k) => { this._cubeUndistortK = Number(k); console.log('[cubeK]', this._cubeUndistortK, '— re-enter the bubble to apply'); };
    }

    // Initializes the viewer, camera, and lighting.
    this.initializeViewer();

    // Add OrbitControls for interacting with the viewer.
    this.initializeControls();

    // Loads the model in the viewer.
    await this.loadModel();

    // Markers may have finished loading while the model was still loading; make sure the
    // current set is in the scene (covers the case where an earlier sync was skipped).
    if (this.scene) this.syncMarkerGroups();

    this.addPointerMoveListener();

    // Animation loop for rendering and controls update.
    this.startAnimationLoop();

    this.isLoading = false;
    this.setStartingCameraPosition();
    this.fitClipPlanesToModel();
    // Emit after fitClipPlanesToModel so the world bounds (for the slice tool) are ready.
    this.$emit('onModelLoaded', this.worldBounds);

    // Background: compute + persist each scan's minimap perimeter if it hasn't been captured yet.
    // Runs on idle so it never competes with the interactive first render.
    this.scheduleCapturePerimeters();

    // Reflect layer-visibility toggles from viewerUiStore onto the meshes. Pinia's
    // $subscribe fires reliably on store mutation (a deep string-path watch was flaky).
    this.layersUnsubscribe = this.viewerUi.$subscribe(() => this.applyLayerVisibility());
  },
  beforeUnmount() {
    // Stop the render loops so they don't keep running (and keep the scene + WebGL context alive)
    // after the viewer is torn down.
    if (this.animationFrameId) cancelAnimationFrame(this.animationFrameId);
    if (this.hotSpotAnimationFrameId) cancelAnimationFrame(this.hotSpotAnimationFrameId);

    if (this.layersUnsubscribe) {
      this.layersUnsubscribe();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.touchTap = null;

    window.removeEventListener('keydown', this.keyDownHandler);
    window.removeEventListener("mouseup", this.mouseUpHandler);
    this.renderer.domElement.removeEventListener('click', this.clickHandler);
    this.renderer.domElement.removeEventListener('dblclick', this.dblClickHandler);
    if (this._bubbleWheelHandler) this.renderer.domElement.removeEventListener('wheel', this._bubbleWheelHandler);
    if (this._bubblePinchHandler) this.renderer.domElement.removeEventListener('touchmove', this._bubblePinchHandler);
    if (this._bubblePinchEndHandler) {
      this.renderer.domElement.removeEventListener('touchend', this._bubblePinchEndHandler);
      this.renderer.domElement.removeEventListener('touchcancel', this._bubblePinchEndHandler);
    }
    this.renderer.domElement.removeEventListener('touchstart', this.touchStartHandler);
    this.renderer.domElement.removeEventListener('touchmove', this.touchMoveHandler);
    this.renderer.domElement.removeEventListener('touchend', this.touchEndHandler);
    this.renderer.domElement.removeEventListener('touchcancel', this.touchEndHandler);
    window.removeEventListener('resize', this.onWindowResize);
    if (this.pointerMoveHandler) window.removeEventListener('pointermove', this.pointerMoveHandler);

    // Free every geometry/material/texture in the scene (point clouds, BVH meshes, camera paths,
    // markers, labels, the pointer sphere) — three.js won't GC these on its own.
    this.disposeObject3D(this.scene);

    // Dispose the controls (they register their own DOM listeners + helper objects).
    this.orbitControls?.dispose();
    this.transformControls?.dispose();

    // Release the WebGL context / GPU resources held by the renderer.
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss?.();
      this.renderer.domElement?.remove();
    }
  },
  methods: {
    initializeViewer() {
      // Create non-reactive properties
      this.scene = new THREE.Scene();
      this.scene.background = new THREE.Color(0x1a1f24)
      this.camera = new THREE.PerspectiveCamera(
        75,
        this.$refs.canvasContainer.clientWidth / this.$refs.canvasContainer.clientHeight,
        0.1,
        50
      );

      this.camera.up.set(0, 0, 1);
      this.camera.position.set(3, 3, 3);
      this.camera.far = 500;
      this.camera.updateProjectionMatrix();

      // preserveDrawingBuffer lets us read the canvas back (toDataURL / drawImage) to crop the
      // street-view annotation thumbnail. Minor perf cost; the viewer isn't fill-rate bound.
      this.renderer = new THREE.WebGLRenderer({ antialias: true, preserveDrawingBuffer: true });
      // Cap the device pixel ratio: point clouds are fragment-bound, so rendering at a 4K/retina
      // DPR of 2–3 means 4–9× the fragment work for little visible gain on points. Clamp to 2.
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.setSize(this.$refs.canvasContainer.clientWidth, this.$refs.canvasContainer.clientHeight);
      this.$refs.canvasContainer.appendChild(this.renderer.domElement);

      // Add lighting for better visualization
      const ambientLight = new THREE.AmbientLight(0xffffff, 40.5);
      this.scene.add(ambientLight);
    },

    syncMarkerGroups() {
      try {
        const incomingIds = new Set(this.markers.map(marker => marker.id));

        for (const [id, existingMarker] of this.renderedMarkers.entries()) {
          if (!incomingIds.has(id)) {
            existingMarker.meshGroup?.removeFromParent();
            // The marker is gone for good (deleted / filtered out) — free its GPU resources.
            this.disposeObject3D(existingMarker.meshGroup);
            this.renderedMarkers.delete(id);
          }
        }

        for (const marker of this.markers) {
          // Annotations live under their scan's group so their stored (scan-local) transform renders
          // at the right world place and follows the scan. Unknown scans fall back to a root container.
          const target = this.scanGroups?.get(marker.scanId)?.group ?? this.getFallbackMarkerContainer();
          const existingMarker = this.renderedMarkers.get(marker.id);

          if (!existingMarker) {
            target.add(marker.meshGroup);
            this.renderedMarkers.set(marker.id, marker);
            continue;
          }

          if (existingMarker !== marker) {
            existingMarker.meshGroup?.removeFromParent();
            target.add(marker.meshGroup);
            this.renderedMarkers.set(marker.id, marker);
          } else {
            // Re-home if it was placed before its scan group existed (markers can load before scans).
            if (marker.meshGroup.parent !== target) {
              marker.meshGroup.removeFromParent();
              target.add(marker.meshGroup);
            }
            marker.meshGroup.updateMatrixWorld(true);
          }
        }

        // Newly added markers default to visible; reflect the current layer toggles so
        // hidden layers (zones/cameras) don't pop in on load.
        this.applyLayerVisibility();

        // Newly added zone fat-lines need their material resolution seeded to the viewport.
        this.sizeAllZoneLines();

        // Camera-position dots scale to the model (markers can load after the model is measured).
        this.sizeCameraApexes();

        if (process.env.NODE_ENV === "development") {
          const anomalies = this.markers.filter(m => m instanceof AnomalyMarker);
          const scanKeys = [...(this.scanGroups?.keys() ?? [])];
          console.log(`[ModelViewer] sync: ${this.markers.length} markers, ${anomalies.length} anomalies; scanGroups=[${scanKeys.map(k => k.slice(0, 8)).join(', ')}]`);
          anomalies.forEach(m => {
            const wp = new THREE.Vector3();
            m.meshGroup?.getWorldPosition(wp);
            console.log(`  anomaly ${m.id?.slice(0, 8)} scan=${m.scanId?.slice(0, 8)} ${this.scanGroups?.has(m.scanId) ? 'nested' : 'FALLBACK'} visible=${m.meshGroup?.visible} world=(${wp.x.toFixed(2)}, ${wp.y.toFixed(2)}, ${wp.z.toFixed(2)})`);
          });
        }
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.error("Error syncing markers:", error);
        }
      }
    },

    // Root container for annotations whose scan isn't part of this version (e.g. markers from
    // another scan loaded by assetId). Lazily created; positions are used as-is (world space).
    getFallbackMarkerContainer() {
      if (!this.markerGroupContainer) {
        this.markerGroupContainer = markRaw(new THREE.Group());
        this.markerGroupContainer.name = "marker-group-container";
        this.scene.add(this.markerGroupContainer);
      }
      return this.markerGroupContainer;
    },

    setupResizeHandler() {
      // Resize the renderer and update the camera aspect ratio on container resize
      this.resizeObserver = new ResizeObserver(() => {
        this.onResize();
      });
      this.resizeObserver.observe(this.$refs.canvasContainer);
    },

    onResize() {
      const el = this.$refs.canvasContainer;
      if (!el || !this.camera || !this.renderer) return;

      const width = el.clientWidth;
      const height = el.clientHeight;
      // A collapsed/hidden container (0 in either dimension) makes aspect NaN/Infinity, which blanks
      // the canvas until reload. Skip degenerate sizes — the observer fires again with real dims.
      if (width === 0 || height === 0) return;

      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(width, height);
      this.sizeAllCameraPaths();
      this.sizeAllZoneLines();
    },

    onWindowResize() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      // Wait 300ms after the last resize event to trigger
      this.resizeTimeout = setTimeout(() => {
        const el = this.$refs.canvasContainer;
        if (!el || !this.camera || !this.renderer) return;

        const width = el.clientWidth;
        const height = el.clientHeight;
        if (width === 0 || height === 0) return;  // skip degenerate sizes (see onResize)

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.renderer.setSize(width, height);
        this.sizeAllCameraPaths();
        this.sizeAllZoneLines();
        this.addPointerMoveListener();

      }, 300);
    },

    initializeControls() {
      this.mouse = new THREE.Vector2();

      THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
      THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;
      THREE.Mesh.prototype.raycast = acceleratedRaycast;

      THREE.BatchedMesh.prototype.computeBoundsTree = computeBatchedBoundsTree;
      THREE.BatchedMesh.prototype.disposeBoundsTree = disposeBatchedBoundsTree;
      THREE.BatchedMesh.prototype.raycast = acceleratedRaycast;

      this.raycaster = new THREE.Raycaster();
      this.raycaster.params.Points.threshold = 0.02;

      this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
      this.orbitControls.enableDamping = true;
      this.orbitControls.dampingFactor = 0.14;
      this.orbitControls.minDistance = 1;
      this.orbitControls.maxDistance = 100;

      // Enable panning
      this.orbitControls.mouseButtons = {
        LEFT: THREE.MOUSE.ROTATE,
        MIDDLE: THREE.MOUSE.PAN,
        RIGHT: THREE.MOUSE.DOLLY,
      };

      try {
        const transformControls = new TransformControls(this.camera, this.renderer.domElement);
        transformControls.addEventListener('dragging-changed', (event) => {
          this.orbitControls.enabled = !event.value;
        });
        this.transformControls = transformControls;
        this.scene.add(this.transformControls.getHelper());
      } catch (error) {
        if (process.env.NODE_ENV === "development") {
          console.log("DEV: Had a problem adding transform controls.", error);
        }
      }

      this.renderer.domElement.addEventListener('click', this.clickHandler);  // done
      this.renderer.domElement.addEventListener('dblclick', this.dblClickHandler);  //done

      // Inside the bubble, the mouse wheel zooms by narrowing the camera FOV (Street-View style),
      // staying centred — OrbitControls dolly is disabled there.
      this._bubbleWheelHandler = this.onBubbleWheel.bind(this);
      this.renderer.domElement.addEventListener('wheel', this._bubbleWheelHandler, { passive: false });
      // Touch equivalent of the wheel: two-finger pinch narrows/widens the FOV inside the bubble
      // (single-finger orbit is handled by OrbitControls, Google-Maps style).
      this._bubblePinchHandler = this.onBubblePinch.bind(this);
      this._bubblePinchEndHandler = this.onBubblePinchEnd.bind(this);
      this.renderer.domElement.addEventListener('touchmove', this._bubblePinchHandler, { passive: false });
      this.renderer.domElement.addEventListener('touchend', this._bubblePinchEndHandler);
      this.renderer.domElement.addEventListener('touchcancel', this._bubblePinchEndHandler);

      // Touch: long-press an anomaly marker on mobile/tablet to open its details popup.
      this.renderer.domElement.addEventListener('touchstart', this.touchStartHandler, { passive: true });
      this.renderer.domElement.addEventListener('touchmove', this.touchMoveHandler, { passive: true });
      this.renderer.domElement.addEventListener('touchend', this.touchEndHandler);
      this.renderer.domElement.addEventListener('touchcancel', this.touchEndHandler);

      window.addEventListener('keydown', this.keyDownHandler);   //done
      window.addEventListener("mouseup", this.mouseUpHandler);  //done
      window.addEventListener('resize', this.onWindowResize);
      // Also refit on container-only resizes (panel/drawer open-close) that don't fire window resize.
      this.setupResizeHandler();
      window.removeEventListener('pointermove', this.pointerMoveHandler, false);

    },

    setStartingCameraPosition() {
      if (this.modelVersion == null) return;

      const mv = this.modelVersion;
      const hasPosition = mv.modelViewPointX != 0 || mv.modelViewPointY != 0 || mv.modelViewPointZ != 0;
      if (!hasPosition) return;

      this.camera.position.set(mv.modelViewPointX, mv.modelViewPointY, mv.modelViewPointZ);

      const hasRotation = mv.modelViewPointRotationX != 0 || mv.modelViewPointRotationY != 0 || mv.modelViewPointRotationZ != 0;
      if (hasRotation) {
        // OrbitControls derives the camera's orientation from (position, target) and ignores
        // camera.rotation. So apply the saved rotation, read the resulting look direction, and
        // convert it into a target point for OrbitControls — otherwise the camera snaps back to
        // looking at the default target (the "looking down instead of across" symptom).
        this.camera.rotation.set(mv.modelViewPointRotationX, mv.modelViewPointRotationY, mv.modelViewPointRotationZ, 'XYZ');
        this.camera.updateMatrixWorld();

        const forward = new THREE.Vector3();
        this.camera.getWorldDirection(forward);

        const distance = this.orbitControls.target.distanceTo(this.camera.position) || 5;
        this.orbitControls.target.copy(this.camera.position).addScaledVector(forward, distance);
      }

      this.orbitControls.update();
    },

    // Grows the camera clip planes and the zoom-out limit to fit the model's actual world bounds
    // (which include each scan's transform, so a scaled-up base scan is covered). Without this a
    // large model is clipped by the fixed far plane / trapped inside the fixed max-distance. Never
    // shrinks below the defaults, so normal-scale models behave exactly as before.
    fitClipPlanesToModel() {
      if (!this.scanGroups || this.scanGroups.size === 0) return;

      const box = new THREE.Box3();
      const tmp = new THREE.Box3();
      for (const { group, pointCloud } of this.scanGroups.values()) {
        const geom = pointCloud?.geometry;
        if (!geom) continue;
        if (!geom.boundingBox) geom.computeBoundingBox();
        group.updateMatrixWorld(true);
        tmp.copy(geom.boundingBox).applyMatrix4(group.matrixWorld);
        box.union(tmp);
      }
      if (box.isEmpty()) return;

      // World-space extent — drives the slice-tool slider ranges (min/max per axis).
      this.worldBounds = {
        min: { x: box.min.x, y: box.min.y, z: box.min.z },
        max: { x: box.max.x, y: box.max.y, z: box.max.z },
      };

      const radius = box.getBoundingSphere(new THREE.Sphere()).radius || 1;
      this.modelRadius = radius;  // used by changeCameraDirection to zoom proportionally to model size
      this.sizeCameraApexes();    // camera-position dots scale with the model

      // Point size proportional to the model so it renders crisp at any scale instead of as fat
      // overlapping blobs. sizeAttenuation is on (world units), and the fixed 0.02 looked huge on a
      // tiny cloud; 0.0005 × radius reproduces ~0.02 on a normal-scale model, so nothing regresses.
      const pointSize = radius * 0.0005;
      for (const { pointCloud } of this.scanGroups.values()) {
        const m = pointCloud?.material;
        if (m && 'size' in m) { m.size = pointSize; m.needsUpdate = true; }
      }

      // maxDistance covers zoom-out — generous (8× radius) so you can pull right back and frame the
      // whole model. far must then reach the model's far side from the furthest camera position
      // (max-distance from target + the model radius), with margin.
      this.orbitControls.maxDistance = Math.max(100, radius * 8);
      // Let the camera get close enough to inspect a point on any-scale model (the fixed min of 1
      // would trap you far from a small/moderately-scaled model and clamp the double-click zoom).
      this.orbitControls.minDistance = Math.max(0.05, radius * 0.005);
      this.camera.far = Math.max(500, this.orbitControls.maxDistance + radius * 2);
      this.camera.near = Math.max(0.1, this.camera.far / 50000);
      this.camera.updateProjectionMatrix();
      this.orbitControls.update();
    },

    mouseUpHandler() {
      // Inspection-area context is now driven by clicking the zone's edge/label (selectInspectionArea),
      // not by flying the camera inside it — so this only reports the camera position now.
      this.emitOnPositionChanged(this.camera);
    },

    emitOnPositionChanged(camera) {
      const position = new CameraPosition(camera);
      this.$emit('onPositionChanged', position);
    },

    dblClickHandler(event) {
      if (this.bubbleActive) return;   // inside the photo bubble: orient/zoom only, no picking
      clearTimeout(this.clickTimeout); // Cancel single click if double click is detected
      this.onDoubleClick(event); // Double click action
    },

    clickHandler(event) {
      if (this.bubbleActive) return;   // inside the photo bubble: orient/zoom only, no picking
      clearTimeout(this.clickTimeout);
      this.clickTimeout = setTimeout(() => {
        this.onClick(event);
      }, 180);
    },

    // Marker selection on touch is a TAP (a single finger down-and-up with little movement). A finger
    // that moves is an orbit/pan — OrbitControls handles that, and we don't select. (Long-press was
    // replaced: it was finicky against OrbitControls and creating markers on touch is disabled.)
    touchStartHandler(event) {
      if (this.bubbleActive) { this.touchTap = null; return; }   // bubble: orient/zoom only, no picking
      if (event.touches.length !== 1) { this.touchTap = null; return; }   // multi-touch = pinch/zoom
      const touch = event.touches[0];
      this.touchTap = { x: touch.clientX, y: touch.clientY, moved: false };
    },

    touchMoveHandler(event) {
      if (!this.touchTap) return;
      if (event.touches.length !== 1) { this.touchTap.moved = true; return; }
      const touch = event.touches[0];
      const moved = Math.hypot(touch.clientX - this.touchTap.x, touch.clientY - this.touchTap.y);
      if (moved > 12) this.touchTap.moved = true;   // it's a drag → orbit/pan, not a tap
    },

    touchEndHandler(event) {
      const tap = this.touchTap;
      this.touchTap = null;
      if (!tap || tap.moved) return;   // moved → not a tap; nothing to select
      const t = event?.changedTouches?.[0];
      const x = t ? t.clientX : tap.x;
      const y = t ? t.clientY : tap.y;
      const marker = this.getMarkerAtScreenPosition(x, y);
      if (marker instanceof HotSpotMarker) {
        this.$emit('mobileHotSpotSelected', marker);
      } else if (marker instanceof AnomalyMarker) {
        this.$emit('mobileMarkerSelected', marker);
      }
    },

    // Raycast at a screen position and return the AnomalyMarker or HotSpotMarker under it (or null).
    getMarkerAtScreenPosition(clientX, clientY) {
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      this.mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

      this.raycaster.precision = 0.00001;
      this.camera.updateMatrixWorld();
      this.raycaster.setFromCamera(this.mouse, this.camera);

      const meshObjects = [];
      this.markers.forEach((marker) => {
        if (marker instanceof AnomalyMarker || marker instanceof HotSpotMarker) {
          const mesh = marker.meshGroup.children.find((child) => child.isMesh);
          if (mesh && marker.meshGroup.visible) {
            meshObjects.push(mesh);
          }
        }
      });

      const intersects = this.raycaster.intersectObjects(meshObjects, true);
      if (intersects.length === 0) return null;

      const selectedMesh = intersects[0].object;
      return this.markers.find(m => m.id == selectedMesh.userData.markerId) || null;
    },

    keyDownHandler(event) {
      switch (event.key) {
        case 'm':
          this.setTransformToMove();
          break;

        case 's':
          this.setTransformToScale();
          break;

        case 'r':
          this.setTransformToRotate();
          break;

        case ' ': {
          // Spacebar toggles walkthrough playback (ignored while typing in a field/dropdown).
          const t = event.target;
          const typing = t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);
          if (this.playbackAvailable && !typing) {
            event.preventDefault();   // stop the page from scrolling
            this.togglePlayback();
          }
          break;
        }
      }
    },

    addPointerMoveListener() {
      if (this.pointerMoveHandler) {
        window.removeEventListener('pointermove', this.pointerMoveHandler);
        this.pointerMoveHandler = null;
      }

      // Touch devices have no hover cursor and can't create markers — skip the placement sphere.
      if (!this.hoverPlacement) return;

      // Create the sphere once and reuse it
      const geometry = new THREE.SphereGeometry(0.05, 32, 32);
      const material = new THREE.MeshBasicMaterial({ color: 0xffff00, opacity: 0.9, transparent: true });
      const sphereCollision = new THREE.Mesh(geometry, material);
      sphereCollision.visible = false;

      if (this.currentSphereCollisionObject != null) {
        this.scene.remove(this.currentSphereCollisionObject);
        this.currentSphereCollisionObject.geometry.dispose();
        this.currentSphereCollisionObject.material.dispose();
        this.currentSphereCollisionObject = null;
      }

      this.currentSphereCollisionObject = sphereCollision;
      this.scene.add(sphereCollision);

      const rect = this.$refs.canvasContainer.getBoundingClientRect();

      // Define the handler as a method property for removal
      this.pointerMoveHandler = (event) => {
        if (this.isTransformActive) return;
        if (!this.scanGroups || this.scanGroups.size === 0) return;

        this.camera.updateMatrixWorld();
        this.camera.updateProjectionMatrix();

        if (event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom) {
          this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
          this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
          this.raycaster.setFromCamera(this.mouse, this.camera);

          sphereCollision.visible = false;

          // Pick across every scan's BVH and take the nearest hit; remember which scan it was on.
          const hit = this.findClosestScanPoint(this.raycaster.ray);
          if (hit) {
            sphereCollision.position.copy(hit.worldPoint);
            sphereCollision.visible = true;
            this.highlightedPoint.copy(hit.worldPoint);
            this.highlightedPointIndex = hit.index;
            this.highlightedScanId = hit.scanId;
          }
        }
      };

      // Add the pointermove event listener
      window.addEventListener('pointermove', this.pointerMoveHandler, false);
    },

    // Finds the closest point-cloud hit across all scans for a world-space ray. Each scan's BVH is
    // in its own local space (scan transform on the group), so the ray is transformed into each
    // mesh's local space and the hit transformed back to world. Returns { worldPoint, scanId, index }.
    findClosestScanPoint(worldRay) {
      const threshold = this.raycaster.params.Points.threshold;
      let best = null;

      for (const [scanId, entry] of this.scanGroups) {
        const bvhMesh = entry.bvhMesh;
        if (!bvhMesh || !bvhMesh.geometry.boundsTree) continue;
        if (entry.group.visible === false) continue;

        bvhMesh.updateMatrixWorld();
        const inverse = new THREE.Matrix4().copy(bvhMesh.matrixWorld).invert();
        const localRay = worldRay.clone().applyMatrix4(inverse);

        // World threshold → mesh-local (the scan may be scaled).
        const scale = new THREE.Vector3();
        bvhMesh.matrixWorld.decompose(new THREE.Vector3(), new THREE.Quaternion(), scale);
        const avgScale = (Math.abs(scale.x) + Math.abs(scale.y) + Math.abs(scale.z)) / 3 || 1;
        const localThreshold = threshold / avgScale;
        const localThresholdSq = localThreshold * localThreshold;

        let closestLocal = Infinity;
        let hitLocal = null;
        let hitIndex = -1;

        bvhMesh.geometry.boundsTree.shapecast({
          boundsTraverseOrder: (box) => box.distanceToPoint(localRay.origin),
          intersectsBounds: (box, isLeaf, score) => {
            if (score > closestLocal) return NOT_INTERSECTED;
            box.expandByScalar(localThreshold);
            return localRay.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED;
          },
          intersectsTriangle: (triangle, triangleIndex) => {
            const dSq = localRay.distanceSqToPoint(triangle.a);
            if (dSq < localThresholdSq) {
              const d = localRay.origin.distanceTo(triangle.a);
              if (d < closestLocal) {
                closestLocal = d;
                hitLocal = triangle.a.clone();
                hitIndex = triangleIndex;
              }
            }
          },
        });

        if (hitLocal) {
          const worldPoint = hitLocal.applyMatrix4(bvhMesh.matrixWorld);
          const worldDist = worldRay.origin.distanceTo(worldPoint);
          if (!best || worldDist < best.worldDist) {
            best = { worldPoint, scanId, index: hitIndex, worldDist };
          }
        }
      }

      return best;
    },

    async loadModel() {
      // markRaw: `modelLoader` is a data() field, so without this the ModelLoader instance becomes
      // a reactive proxy — then `this` inside its methods is the proxy and every THREE object it
      // creates (the point clouds) is reactive, which throws the "modelViewMatrix is read-only"
      // proxy-invariant error when the renderer touches them.
      this.modelLoader = markRaw(new ModelLoader(this.scene, this.camera));

      const onPhase = (phase, value) => {
        if (phase === 'download') {
          this.loadingPhase = 'download';
          this.downloadProgress = value;
        } else if (phase === 'processing') {
          this.loadingPhase = 'processing';
        }
      };

      // Render every scan in the version. The primary (base) scan is the reference frame markers
      // are placed against, so it loads at identity and provides the BVH pick-mesh; supplementary
      // scans are display-only and positioned by their stored transform. Falls back to the single
      // primary model for older API responses without the `scans` projection.
      const scans = (this.modelVersion.scans && this.modelVersion.scans.length)
        ? this.modelVersion.scans
        : [{ scanId: this.modelVersion.associatedScan, modelPath: this.modelVersion.modelPath, transform: null }];

      let primaryIndex = scans.findIndex(s => s.scanId === this.modelVersion.associatedScan);
      if (primaryIndex < 0) primaryIndex = 0;

      // Each scan is its own group carrying the scan's transform; its point cloud + BVH pick-mesh
      // are children, and (in syncMarkerGroups) so are its annotations — so annotations stored in
      // scan-local coordinates render at the right world place and follow the scan if re-aligned.
      this.scanGroups = new Map();

      // Reset walkthrough playback for the fresh model (scan set may differ).
      this.isPlaying = false;
      this.isPaused = false;
      this.playbackAvailable = false;
      this.playbackScans = [];
      this.selectedPlaybackScanId = null;
      this.playbackProgress = 0;
      this._playCurve = null;
      this.cleanupBubbleAndPeg();   // bubble/pegman resources are per-model
      this.emitPlaybackState();

      // Start every scan's load together so their downloads overlap (the parse itself still runs on
      // the main thread, so parses serialise — but download is the usual bottleneck on big clouds).
      // Only the primary scan reports to the loading overlay, so its progress ring stays clean while
      // supplementary scans download in the background. Results keep scan order (Promise.all), so the
      // scene setup below — which depends on primary-first refs — is unchanged.
      const loaded = await Promise.all(scans.map((scan, i) =>
        this.modelLoader.loadPLYModelAsPointCloud(scan.modelPath, i === primaryIndex ? onPhase : null, true)
          .then((result) => ({ i, scan, result }))
          .catch((error) => ({ i, scan, error }))
      ));

      for (const { i, scan, result, error } of loaded) {
        const isPrimary = i === primaryIndex;

        if (error) {
          // A supplementary scan failing to load shouldn't break the viewer; the primary is what
          // markers/picking ultimately fall back to.
          if (process.env.NODE_ENV === "development") {
            console.error(`DEV: Error loading scan ${scan.scanId} (primary=${isPrimary}):`, error);
          }
          if (isPrimary) break;
          continue;
        }

        const { pointCloud, bvhMesh } = result;

        // markRaw so Vue never wraps these three.js objects in a reactive proxy (a proxied
        // renderable throws "modelViewMatrix is read-only" during render).
        const group = markRaw(new THREE.Group());
        markRaw(pointCloud);
        if (bvhMesh) markRaw(bvhMesh);

        group.name = `scan:${scan.scanId}`;
        this.applyScanTransform(group, scan.transform);

        // Install the trim shader before the cloud is rendered for the first time so the
        // onBeforeCompile hook runs in the initial compile, no recompile churn.
        installCullShader(pointCloud.material);

        group.add(pointCloud);              // re-parents the cloud from the scene into the group
        if (bvhMesh) {
          bvhMesh.visible = false;          // pick-only proxy (degenerate tris render nothing anyway)
          group.add(bvhMesh);               // nested so its matrixWorld includes the scan transform
        }
        this.scene.add(group);

        this.scanGroups.set(scan.scanId, { group, pointCloud, bvhMesh });

        if (isPrimary) {
          // Back-compat references: the primary is the "a model is loaded" marker + default pick.
          this.pointCloud = pointCloud;
          this.bvhMesh = bvhMesh;
        }

        // Camera-helper cull + walking-path setup.
        //
        // For scans WITH a saved trim radius: block on the cull so the first frame we show is
        // already pruned — no flash of the full untrimmed cloud. We hide the cloud until the cull's
        // applied (or until the helper fetch fails, in which case we fall back to the full cloud).
        //
        // For scans WITHOUT a saved trim: keep the fire-and-forget behaviour so the cloud is
        // interactive the instant it parses, and the walking-path polyline fades in when helpers
        // arrive. Walk-path colour is keyed by scan index to match the alignment editor.
        const pathColor = CAMERA_PATH_COLORS[i % CAMERA_PATH_COLORS.length];

        const needsTrim = (scan.trimRadius || 0) > 0;
        if (needsTrim) {
          pointCloud.visible = false;
          try {
            await this.attachScanCameraHelpers(scan, pathColor);
          } catch (e) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`DEV: camera-helper setup failed for scan ${scan.scanId} — falling back to untrimmed`, e);
            }
          } finally {
            pointCloud.visible = true;
          }
        } else {
          this.attachScanCameraHelpers(scan, pathColor).catch((e) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn(`DEV: camera-helper setup failed for scan ${scan.scanId}`, e);
            }
          });
        }
      }
    },

    // Fetches the scan's camera helpers, precomputes the per-vertex distance attribute, applies
    // the persisted trim radius, and attaches the walking-path polyline (visible per the current
    // layer toggle). Backgrounded from loadModel so the cloud is interactive immediately.
    async attachScanCameraHelpers(scan, pathColor) {
      const entry = this.scanGroups?.get(scan.scanId);
      if (!entry) return;

      const rawHelpers = await AssetService.getCameraHelpersAsync(scan.scanId);
      if (!rawHelpers?.length) return;

      // Keep the full (non-deduped) helper set so the bubble view can rebuild a capture point's 6
      // cube faces from their individual poses; and surface the pegman now that capture points exist.
      entry.rawHelpers = rawHelpers;
      this.capturePointsAvailable = true;

      // 360 captures emit 6 helpers per capture point (one per cubemap face); collapse so the
      // path doesn't draw 6 stacked nodes and the distance compute isn't repeated work.
      const deduped = dedupeCubemapHelpers(rawHelpers)
        .sort((a, b) => (a.imageName ?? '').localeCompare(b.imageName ?? '', undefined, { numeric: true }));
      const positions = helperPositions(deduped);
      if (!positions.length) return;

      // Same entry might have been removed during reload between fetch and now — bail safely.
      if (this.scanGroups?.get(scan.scanId) !== entry) return;

      await attachCullDistances(entry.pointCloud.geometry, positions);
      setMaterialTrimRadius(entry.pointCloud.material, scan.trimRadius || 0);

      // Keep the ordered local capture positions on the entry so playback can fly the camera along
      // them (transformed to world space at play time), independent of the walking-path layer toggle.
      entry.pathPositions = positions;
      this.registerPlaybackScan(scan);

      // Draw the walking path from a thinned "keyframe" set so the trail stays clean; peg-drop
      // snapping, playback and cull distances all keep using the full capture set above.
      const pathCenters = decimatePathCenters(positions);
      const path = createCameraPath(pathCenters, pathColor ? { color: pathColor } : {});
      if (path) {
        markRaw(path);
        this.sizeCameraPath(path);  // fat-line pixel width needs the live viewport size
        path.visible = this.viewerUi.layers.walkingPath;
        entry.group.add(path);
        entry.cameraPath = path;
      }
    },

    // Fat camera-path lines (LineMaterial) derive their pixel width from the viewport resolution,
    // so it must be (re)set on creation and whenever the canvas resizes.
    sizeCameraPath(path) {
      const el = this.$refs.canvasContainer;
      if (!el || !path?.material?.resolution) return;
      path.material.resolution.set(el.clientWidth, el.clientHeight);
    },

    sizeAllCameraPaths() {
      if (!this.scanGroups) return;
      for (const entry of this.scanGroups.values()) this.sizeCameraPath(entry.cameraPath);
    },

    // Positions a scan group by its stored transform (identity when null). The group auto-updates
    // its matrix, so setting position/rotation/scale is enough.
    applyScanTransform(object, transform) {
      const p = transform?.position ?? {};
      const r = transform?.rotation ?? {};
      const s = transform?.scale ?? {};
      object.position.set(p.x ?? 0, p.y ?? 0, p.z ?? 0);
      object.rotation.set(r.x ?? 0, r.y ?? 0, r.z ?? 0, r.order ?? 'XYZ');
      object.scale.set(s.x ?? 1, s.y ?? 1, s.z ?? 1);
    },

    // World point → the given scan's local coordinates (for storing annotations scan-relative).
    toScanLocal(scanId, worldPoint) {
      const entry = this.scanGroups?.get(scanId);
      if (!entry) return worldPoint.clone();
      entry.group.updateMatrixWorld(true);
      return entry.group.worldToLocal(worldPoint.clone());
    },

    startAnimationLoop() {
      const animate = () => {
        // Track the frame id so beforeUnmount can cancel the loop — otherwise it keeps rendering
        // (and pins the scene + WebGL context in memory) after the user leaves the viewer.
        this.animationFrameId = requestAnimationFrame(animate);
        // Walkthrough playback glides the orbit PIVOT along the capture path (translating the camera
        // with it so the user's orbit offset is preserved). OrbitControls still runs every frame, so
        // the user can orbit while flying and the view holds its direction when they let go.
        if (this.isPlaying && !this.isPaused) this.updatePlaybackFrame();
        this.orbitControls.update();
        this.markers.forEach(marker => {
          if (marker instanceof HotSpotMarker) {
            marker.animate();
          }
        });
        // Flow the dashes along each scan's walk path to show the capture direction.
        if (this.scanGroups) {
          for (const entry of this.scanGroups.values()) {
            const material = entry.cameraPath?.material;
            if (material) material.dashOffset -= 0.015;
          }
        }
        this.renderer.render(this.scene, this.camera);
      };
      animate();
    },

    // ---------------------------------------------------------------- Walkthrough playback
    // Glides the ORBIT PIVOT along the capture path while the camera keeps whatever offset the user
    // set — so the user can orbit freely during the fly-through and the view holds its direction when
    // released. Motion is a centripetal Catmull-Rom spline (plus a light pre-smoothing pass) through
    // the ordered camera-helper positions, so it drifts smoothly instead of jerking side-to-side.

    // Broadcasts the current playback state to the parent (drives the Playback tool card in ViewAsset).
    emitPlaybackState() {
      this.$emit('onPlaybackState', {
        available: this.playbackAvailable,
        playing: this.isPlaying,
        paused: this.isPaused,
        progress: this.playbackProgress,
        speed: this.playbackSpeed,
        scans: this.playbackScans.map(s => ({ scanId: s.scanId, name: s.name })),
        selectedScanId: this.selectedPlaybackScanId,
      });
    },

    // Registers a scan as walkable (once its camera path is attached) and picks a sensible default
    // selection (the primary scan, else the first that appears).
    registerPlaybackScan(scan) {
      if (!scan?.scanId || this.playbackScans.some(s => s.scanId === scan.scanId)) return;
      const isPrimary = scan.scanId === this.modelVersion?.associatedScan;
      const name = scan.name || (isPrimary ? 'Primary scan' : `Scan ${String(scan.scanId).slice(0, 6)}`);
      this.playbackScans.push({ scanId: scan.scanId, name });
      if (isPrimary || !this.selectedPlaybackScanId) this.selectedPlaybackScanId = scan.scanId;
      this.playbackAvailable = true;
      this.emitPlaybackState();
    },

    // Switches which scan's path is walked; invalidates the current spline and resets playback.
    onSelectPlaybackScan(scanId) {
      if (scanId === this.selectedPlaybackScanId) return;
      if (this.bubbleActive) this.exitBubble();   // leave a bubble that belonged to the old path
      this.selectedPlaybackScanId = scanId;
      this.stopPlayback();   // emits state
      this._playCurve = null;
    },

    // Builds the smoothed world-space path spline for the SELECTED scan. Returns true if playable.
    buildPlaybackPath() {
      const scanId = this.selectedPlaybackScanId || this.playbackScans[0]?.scanId;
      const entry = scanId ? this.scanGroups?.get(scanId) : null;
      const local = entry?.pathPositions;
      if (!local || local.length < 2) { this._playCurve = null; this._playTotal = 0; return false; }

      entry.group.updateMatrixWorld(true);
      const m = entry.group.matrixWorld;
      const raw = local.map(p => new THREE.Vector3(p.x, p.y, p.z).applyMatrix4(m));

      const smoothed = this.smoothPathPoints(raw, 2);
      this._playCurve = new THREE.CatmullRomCurve3(smoothed, false, 'centripetal');
      this._playTotal = this._playCurve.getLength();   // also caches arc-lengths for getPointAt
      return this._playTotal > 0;
    },

    // Light moving-average smoothing (endpoints fixed) to damp the operator's hand wobble before the
    // spline, so the fly-through doesn't bob left/right/up/down on every capture point.
    smoothPathPoints(points, passes = 2) {
      let current = points;
      for (let pass = 0; pass < passes; pass++) {
        const next = current.map((p, i) => {
          if (i === 0 || i === current.length - 1) return p.clone();
          return current[i - 1].clone().multiplyScalar(0.25)
            .add(current[i].clone().multiplyScalar(0.5))
            .add(current[i + 1].clone().multiplyScalar(0.25));
        });
        current = next;
      }
      return current;
    },

    // Moves the orbit pivot (and the camera with it, preserving the user's offset) to an arc-length
    // distance along the path. OrbitControls.update() then applies any user orbit around it.
    movePivotTo(distance) {
      if (!this._playCurve || this._playTotal <= 0) return;
      const u = Math.min(Math.max(distance / this._playTotal, 0), 1);
      const p = this._playCurve.getPointAt(u);
      const dp = p.clone().sub(this.orbitControls.target);
      this.orbitControls.target.add(dp);
      this.camera.position.add(dp);
    },

    // Frames the first playback frame: zoomed in as far as the controls/near plane allow, looking
    // straight down the route (no tilt). movePivotTo then translates the camera with the pivot, so
    // the user can orbit/zoom freely as it flies.
    frameStartOfPath() {
      if (!this._playCurve || this._playTotal <= 0) return;
      const u = Math.min(Math.max((this._playDistance || 0) / this._playTotal, 0), 1);
      const p = this._playCurve.getPointAt(u);

      // Forward heading down the walk. The instantaneous first tangent can be noisy/degenerate on
      // closely-spaced captures, so average the direction over a short lookahead so we reliably face
      // the way the walk goes (falling back to the raw tangent if the lookahead collapses).
      const lookAhead = Math.min(1, u + Math.max(0.03, (this.modelRadius * 0.1) / this._playTotal));
      let dir = this._playCurve.getPointAt(lookAhead).sub(p);
      if (dir.lengthSq() < 1e-9) dir = this._playCurve.getTangentAt(u);
      dir.normalize();

      const minD = this.orbitControls?.minDistance || 0;
      const near = (this.camera?.near || 0.1) * 3;                     // stay off the near plane
      const scale = (this.modelRadius && this.modelRadius > 0) ? this.modelRadius * 0.01 : 0.2;
      const d = Math.max(minD, near, scale);
      // Sit just behind the start and aim the target a step further down the path, so the opening
      // frame looks straight along the walking direction rather than keeping the prior orbit angle.
      this.orbitControls.target.copy(p).add(dir.clone().multiplyScalar(d));
      this.camera.position.copy(p).add(dir.clone().multiplyScalar(-d));
      this.camera.updateProjectionMatrix?.();
    },

    // Frees the bubble/pegman resources on model reset.
    cleanupBubbleAndPeg() {
      if (this.bubbleActive) this.exitBubble();
      this.restoreWalkingPaths();
      if (this._pegHighlight) {
        this.scene?.remove(this._pegHighlight);
        this._pegHighlight.material?.map?.dispose?.(); this._pegHighlight.material?.dispose?.();
        this._pegHighlight = null;
      }
      this.capturePointsAvailable = false;
      this.pegDragging = false;
      this.pegHasCandidate = false;
      this._bubbleFramePaths = null;  // re-fetched for the next model
      this._equirectPathsByScan = null;
      this._capturesByScan = null;    // capture order is per-model
      this._visibilityByScan = null;  // visibility index is per-model
    },

    // ---- In-viewer 360 "bubble" view ----

    // The 360 capture points across every loaded scan, grouped into { scanId, base, faces, world }.
    // "faces" are the (up to) 6 cube-face helpers sharing a base name; "world" is the capture centre.
    collectCapturePoints() {
      const faceRe = /^(.+?)[_-](front|back|left|right|up|down|top|bottom|px|nx|py|ny|pz|nz)(\.[^.]+)?$/i;
      const pts = [];
      if (!this.scanGroups) return pts;

      for (const [scanId, entry] of this.scanGroups) {
        if (!entry.rawHelpers?.length) continue;
        entry.group.updateMatrixWorld(true);
        const gm = entry.group.matrixWorld;
        const groups = new Map();
        for (const h of entry.rawHelpers) {
          const name = h?.imageName ?? '';
          const m = name.match(faceRe);
          const base = (m ? m[1] : name).toLowerCase();
          if (!groups.has(base)) groups.set(base, []);
          groups.get(base).push(h);
        }
        for (const [base, faces] of groups) {
          const p = faces[0]?.transform?.position;
          const world = new THREE.Vector3(p?.x || 0, p?.y || 0, p?.z || 0).applyMatrix4(gm);
          pts.push({ scanId, base, faces, world });
        }
      }
      return pts;
    },

    // Steps inside a specific capture point: rebuilds its cube faces as inward-facing photo planes
    // around the capture centre, drops the camera in the middle to look around, hides the point
    // clouds, and leaves the anomaly markers overlaid.
    async openBubbleAt(scanId, faces, centerWorld, base) {
      const entry = scanId ? this.scanGroups?.get(scanId) : null;
      if (!entry || !faces?.length || !centerWorld) return;

      entry.group.updateMatrixWorld(true);
      const gm = entry.group.matrixWorld;

      // Every entry/step bumps an epoch; exiting bumps it too. The awaits below (URL signing) can resolve
      // AFTER the user has exited or stepped away — which would otherwise add the photo planes back over the
      // restored point cloud (the "images don't unload" bug). So bail if our epoch went stale before building.
      const epoch = (this._bubbleEpoch = (this._bubbleEpoch || 0) + 1);

      // Clear any previous bubble (re-drop) but keep the first saved camera state.
      if (this._bubbleGroup) {
        this.scene?.remove(this._bubbleGroup);
        this._bubbleGroup.traverse(o => { o.geometry?.dispose?.(); o.material?.map?.dispose?.(); o.material?.dispose?.(); });
        this._bubbleGroup = null;
      }

      // Only 360 captures (6 named cube faces) can have an equirect panorama — a single-lens (mono)
      // capture never does, so don't even query for one; go straight to the single-frame plane.
      const is360 = faces.some(h => !!cubeFaceName(h.imageName));

      // For 360, prefer a single equirectangular panorama when one exists (seamless, cheaper); otherwise
      // the 6 cube faces. A single-lens (mono) capture is ONE photo — show it FLAT (2D), not in 3D, so a
      // drawn box is a direct pixel crop.
      this.bubbleFlatUrl = null;
      this.bubbleFlatName = null;
      const equirectUrl = is360 ? await this.getEquirectUrl(scanId, base) : null;
      if (this._bubbleEpoch !== epoch) return;   // exited/stepped away during the await — don't build
      // Which frame the drawn region must be interpreted in: the equirect sphere is world-aligned, the
      // stitched cube uses CUBE_FACE_LAYOUT. buildBubblePolygonProvenance picks the face accordingly.
      this._bubbleIsEquirect = !!equirectUrl;
      if (equirectUrl) {
        this.buildEquirectSphere(centerWorld, equirectUrl);
      } else if (is360) {
        const urlByStem = await this.getCubeFrameUrls(scanId);
        if (this._bubbleEpoch !== epoch) return;
        this.buildBubbleFaces({ faces, center: centerWorld }, gm, urlByStem);
      } else {
        const urlByStem = await this.getCubeFrameUrls(scanId);
        if (this._bubbleEpoch !== epoch) return;
        const face = faces[0];
        this.bubbleFlatName = face?.imageName || null;
        this.bubbleFlatUrl = face ? (urlByStem[stemOf(face.imageName)] || null) : null;
        this.flatZoom = 1; this.flatPanX = 0; this.flatPanY = 0;
      }
      if (!this._bubbleGroup && !this.bubbleFlatUrl) return;

      // Capture-sequence position along this scan's walk — drives the walk arrows and the entry heading.
      this._bubbleScanId = scanId;
      this._bubbleCaptures = this.orderedCapturesForScan(scanId);
      this._bubbleIndex = this._bubbleCaptures.findIndex(c => c.base === base);
      this.bubbleCaptureCount = this._bubbleCaptures.length;
      this.bubbleCaptureIndex = this._bubbleIndex;
      // Labels for the quick-hop menu: the frame number from each capture's filename (fallback: position).
      this.bubbleCaptureInfo = this._bubbleCaptures.map((c, i) => ({ index: i, label: this.captureLabelOf(c.base, i) }));

      if (!this._bubbleSaved) {
        this._bubbleSaved = {
          pos: this.camera.position.clone(),
          quat: this.camera.quaternion.clone(),
          target: this.orbitControls.target.clone(),
          enablePan: this.orbitControls.enablePan,
          enableZoom: this.orbitControls.enableZoom,
          minDistance: this.orbitControls.minDistance,
          fov: this.camera.fov,
          rotateSpeed: this.orbitControls.rotateSpeed,
          cloud: [],
          paths: [],
          markers: [],
        };
        // Inside the photo we want just the photo: hide point clouds, walking paths, and the
        // anomaly/hotspot markers. Their prior visibility is saved and restored on exit.
        for (const e of this.scanGroups.values()) {
          if (e.pointCloud) { this._bubbleSaved.cloud.push([e.pointCloud, e.pointCloud.visible]); e.pointCloud.visible = false; }
          if (e.cameraPath) { this._bubbleSaved.paths.push([e.cameraPath, e.cameraPath.visible]); e.cameraPath.visible = false; }
        }
        for (const m of this.markers) {
          if ((m instanceof AnomalyMarker || m instanceof HotSpotMarker) && m.meshGroup) {
            this._bubbleSaved.markers.push([m.meshGroup, m.meshGroup.visible]);
            m.meshGroup.visible = false;
          }
        }
      }

      // Entry heading: a 360 capture faces ALONG the walk (look down the path); a single-lens capture
      // faces the photo itself (its frame forward, set in buildBubbleFaces) so you're looking straight
      // at the image on arrival. Orbit target a hair in front so dragging rotates around the capture
      // point; zoom/pan off.
      const heading = (is360 ? this.bubbleWalkHeading(centerWorld) : null)
        || this._bubbleForward || new THREE.Vector3(0, 1, 0);
      this.camera.position.copy(centerWorld);
      this.orbitControls.target.copy(centerWorld.clone().addScaledVector(heading, Math.max(this.modelRadius * 0.001, 1e-3)));
      this.orbitControls.enablePan = false;
      this.orbitControls.enableZoom = false;
      this.orbitControls.minDistance = 0;
      // Enter a touch wider than the normal camera FOV so you see more of the scene on arrival; the
      // mouse wheel still narrows/widens from here, and the original FOV is restored on exit.
      this.camera.fov = 78;
      this.camera.updateProjectionMatrix();
      this.updateBubbleRotateSpeed();   // slow the look-around as you zoom in for fine aiming

      if (this.isPlaying) this.isPaused = true;   // pause the walk while inside the bubble
      this.bubbleActive = true;
      this.emitPlaybackState();
    },

    // Step to the previous / next capture along this scan's walk (dir = -1 / +1), staying in the bubble.
    // Handy mid-re-capture: if the current photo can't quite frame the marker, move to a neighbour. Any
    // in-progress draw is cleared (it was anchored to the old view); the re-image target + aim carry over.
    // Frame-number label for a capture base (e.g. "...frame00040..." → "Frame 40"); position as a fallback.
    captureLabelOf(base, index) {
      const m = /frame(\d{3,})/i.exec(base || '');
      return m ? `Frame ${Number(m[1])}` : `Frame ${index + 1}`;
    },
    // Step one capture along the walk (prev/next arrows).
    stepBubble(dir) {
      return this.jumpToCapture((this._bubbleIndex ?? 0) + dir);
    },
    // Hop straight to a capture by its index along the walk (the quick-hop menu), clamped to range.
    async jumpToCapture(index) {
      const caps = this._bubbleCaptures || [];
      if (!caps.length) return;
      const next = Math.min(Math.max(index | 0, 0), caps.length - 1);
      if (next === this._bubbleIndex) return;
      const cap = caps[next];
      this.clearBubbleDraw?.();
      await this.openBubbleAt(this._bubbleScanId, cap.faces, cap.world, cap.base);

      // Keep the marker being re-imaged in view from the new capture.
      const marker = this._reimageMarker;
      const entry = marker ? this.scanGroups?.get(marker.scanId) : null;
      if (entry && this.orbitControls) {
        entry.group.updateMatrixWorld(true);
        const p = marker.transform?.position ?? {};
        const mw = new THREE.Vector3(p.x || 0, p.y || 0, p.z || 0).applyMatrix4(entry.group.matrixWorld);
        const d = mw.clone().sub(cap.world);
        if (d.lengthSq() > 1e-9) {
          this.orbitControls.target.copy(cap.world.clone().addScaledVector(d.normalize(), Math.max(this.modelRadius * 0.001, 1e-3)));
        }
      }
    },

    // Horizontal "forward along the walk" heading at the current capture: toward the next capture, or
    // (at the end) continuing the direction we came from. Null if there's nothing to derive it from.
    bubbleWalkHeading(center) {
      const caps = this._bubbleCaptures || [];
      const i = this._bubbleIndex ?? -1;
      if (i < 0) return null;
      let d = null;
      if (i < caps.length - 1) d = caps[i + 1].world.clone().sub(center);
      else if (i > 0) d = center.clone().sub(caps[i - 1].world);
      if (!d) return null;
      d.z = 0;
      return d.lengthSq() < 1e-9 ? null : d.normalize();
    },

    // Ordered (capture-sequence), deduped capture points for a scan: { base, faces, world }. Sorted by
    // the capture base so index ±N steps along the walk. Cached per scan; world uses the current group
    // transform (cleared on model reset). Mirrors collectCapturePoints but per-scan and ordered.
    orderedCapturesForScan(scanId) {
      this._capturesByScan ||= {};
      if (this._capturesByScan[scanId]) return this._capturesByScan[scanId];

      const entry = this.scanGroups?.get(scanId);
      const out = [];
      if (entry?.rawHelpers?.length) {
        entry.group.updateMatrixWorld(true);
        const gm = entry.group.matrixWorld;
        const faceRe = /^(.+?)[_-](front|back|left|right|up|down|top|bottom|px|nx|py|ny|pz|nz)(\.[^.]+)?$/i;
        const groups = new Map();
        for (const h of entry.rawHelpers) {
          const name = h?.imageName ?? '';
          const m = name.match(faceRe);
          const b = (m ? m[1] : name).toLowerCase();
          if (!groups.has(b)) groups.set(b, []);
          groups.get(b).push(h);
        }
        for (const [b, faces] of groups) {
          const p = faces[0]?.transform?.position;
          const world = new THREE.Vector3(p?.x || 0, p?.y || 0, p?.z || 0).applyMatrix4(gm);
          out.push({ base: b, faces, world });
        }
        out.sort((a, b) => a.base.localeCompare(b.base, undefined, { numeric: true }));
      }
      this._capturesByScan[scanId] = out;
      return out;
    },

    // Street-View-style zoom: while in the bubble the wheel narrows/widens the camera FOV (magnify),
    // staying centred. Outside the bubble it does nothing (OrbitControls handles the wheel).
    onBubbleWheel(event) {
      if (!this.bubbleActive || !this.camera) return;
      event.preventDefault();
      const fov = this.camera.fov + event.deltaY * 0.05;
      this.camera.fov = Math.min(100, Math.max(25, fov));   // allow a wider zoom-out than the ~78 entry
      this.camera.updateProjectionMatrix();
      this.updateBubbleRotateSpeed();   // re-scale look-around sensitivity to the new zoom level
    },

    // Two-finger pinch inside the bubble → narrow/widen FOV (mirror of onBubbleWheel for touch).
    // Spreading fingers (distance grows) zooms in; pinching together zooms out.
    onBubblePinch(event) {
      if (!this.bubbleActive || !this.camera || event.touches.length !== 2) {
        this._bubblePinchDist = null;
        return;
      }
      event.preventDefault();
      const [a, b] = event.touches;
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      if (this._bubblePinchDist != null) {
        const delta = this._bubblePinchDist - dist;   // fingers apart → delta<0 → fov shrinks → zoom in
        this.camera.fov = Math.min(100, Math.max(25, this.camera.fov + delta * 0.1));
        this.camera.updateProjectionMatrix();
        this.updateBubbleRotateSpeed();
      }
      this._bubblePinchDist = dist;
    },

    onBubblePinchEnd() {
      this._bubblePinchDist = null;
    },

    // Scale the look-around (OrbitControls rotate) sensitivity to the current bubble zoom. Zoom in
    // narrows the FOV, which magnifies the scene, so a fixed pixels→angle mapping feels twitchy when
    // zoomed in. Scaling rotateSpeed with FOV keeps the on-screen motion roughly constant — slower
    // rotation the further you zoom in, for fine street-view aiming; back to normal when zoomed out.
    updateBubbleRotateSpeed() {
      if (!this.orbitControls) return;
      const BUBBLE_ENTRY_FOV = 78;   // the "normal" zoom level rotate speed is calibrated against
      const ratio = this.camera.fov / BUBBLE_ENTRY_FOV;
      // Clamp so the widest zoom never speeds past normal and the tightest still moves usably.
      this.orbitControls.rotateSpeed = Math.min(1, Math.max(0.25, ratio));
    },

    // ---- Pegman: drag the PPE figure onto a walking path to drop into that photo bubble ----

    // Public: start the Street-view pegman drag from an external control (the viewer tool rail's
    // Street-view button). The drag ghost tracks the cursor and the drop logic is identical to the
    // built-in pegman — only the drag's origin differs.
    startPegDrag(event) {
      if (!this.capturePointsAvailable) return;
      this.onPegPointerDown(event);
    },

    onPegPointerDown(event) {
      event.preventDefault();
      if (this.bubbleActive) return;
      this.pegDragging = true;
      this.pegHasCandidate = false;
      this.pegCursor = { x: event.clientX, y: event.clientY };
      this._pegPoints = this.collectCapturePoints();
      this._pegCandidate = null;
      this._pegRingScale = this.computePegRingScale(this._pegPoints);
      this.showWalkingPathsForDrag();   // temporary guidance while positioning
      this._onPegMove ||= this.onPegPointerMove.bind(this);
      this._onPegUp ||= this.onPegPointerUp.bind(this);
      window.addEventListener('pointermove', this._onPegMove);
      window.addEventListener('pointerup', this._onPegUp);
    },

    onPegPointerMove(event) {
      if (!this.pegDragging) return;
      this.pegCursor = { x: event.clientX, y: event.clientY };
      this.updatePegCandidate(event.clientX, event.clientY);
    },

    onPegPointerUp() {
      if (!this.pegDragging) return;
      this.pegDragging = false;
      window.removeEventListener('pointermove', this._onPegMove);
      window.removeEventListener('pointerup', this._onPegUp);
      const cand = this._pegCandidate;
      this.setPegCandidate(null);
      this.restoreWalkingPaths();   // drop (or cancel) → put the walking path back how it was
      if (cand) this.enterBubbleWithTransition(cand);
    },

    // Force every scan's walking path visible while the pegman is being positioned, remembering the
    // prior visibility so it can be restored on drop/cancel.
    showWalkingPathsForDrag() {
      if (!this.scanGroups) return;
      this._pegPathSaved = [];
      for (const e of this.scanGroups.values()) {
        if (e.cameraPath) {
          this._pegPathSaved.push([e.cameraPath, e.cameraPath.visible]);
          e.cameraPath.visible = true;
        }
      }
    },

    restoreWalkingPaths() {
      for (const [p, vis] of this._pegPathSaved || []) p.visible = vis;
      this._pegPathSaved = null;
    },

    // A sensible highlight-ring diameter: based on the spacing between capture points along the
    // path (median nearest-neighbour distance) rather than the whole-model radius, so the ring is a
    // consistent, path-relative size instead of huge on big scans.
    computePegRingScale(points) {
      const pts = points || [];
      if (pts.length < 2) return this.modelRadius > 0 ? this.modelRadius * 0.06 : 1.5;
      const gaps = [];
      for (let i = 0; i < pts.length; i++) {
        let best = Infinity;
        for (let j = 0; j < pts.length; j++) {
          if (i === j) continue;
          const d = pts[i].world.distanceToSquared(pts[j].world);
          if (d < best) best = d;
        }
        if (best < Infinity) gaps.push(Math.sqrt(best));
      }
      gaps.sort((a, b) => a - b);
      const median = gaps[Math.floor(gaps.length / 2)] || 1;
      return median * 1.6;   // ring a touch larger than the gap between adjacent capture points
    },

    // Street-View-style transition: blur + zoom the current view toward the drop point, build the
    // bubble behind the blur, then clear it to reveal the photo.
    async enterBubbleWithTransition(cand) {
      if (this.bubbleTransition) return;
      const el = this.$refs.canvasContainer;
      if (el) {
        const rect = el.getBoundingClientRect();
        const v = cand.world.clone().project(this.camera);
        this.bubbleZoomOrigin = `${(v.x * 0.5 + 0.5) * rect.width}px ${(-v.y * 0.5 + 0.5) * rect.height}px`;
      }
      const nextFrame = () => new Promise(r => requestAnimationFrame(r));
      this.bubbleTransition = 'in';   // point cloud zooms in + blurs toward the drop point
      try {
        await new Promise(r => setTimeout(r, 400));                    // let the point cloud zoom in
        await this.openBubbleAt(cand.scanId, cand.faces, cand.world, cand.base);  // swap to the (blurred) bubble, start loads
        // The zoom/blur IS the loader: hold until the face images finish (capped so it never hangs).
        const cap = new Promise(r => setTimeout(r, 8000));
        await Promise.race([Promise.all(this._bubblePendingTextures || []), cap]);
        await nextFrame();
        // Reveal: snap to the sharp bubble instantly — no zoom-out/settle afterwards.
        this.bubbleNoTransition = true;
        this.bubbleTransition = '';
        await nextFrame(); await nextFrame();
      } finally {
        this.bubbleNoTransition = false;
      }
    },

    // Exit: blur + zoom the bubble OUT, then simply show the point cloud (no after-effects).
    async exitBubbleWithTransition() {
      if (this.bubbleTransition) { this.exitBubble(); return; }
      const nextFrame = () => new Promise(r => requestAnimationFrame(r));
      this.bubbleZoomOrigin = 'center';
      this.bubbleTransition = 'out';   // bubble zooms out + blurs
      try {
        await new Promise(r => setTimeout(r, 520));
        // Snap straight to the sharp point cloud — no settle.
        this.bubbleNoTransition = true;
        this.exitBubble();              // restore the point cloud + camera
        this.bubbleTransition = '';
        await nextFrame(); await nextFrame();
      } finally {
        this.bubbleNoTransition = false;
      }
    },

    // Nearest capture point to the cursor in screen space (like Street View snapping to a road).
    updatePegCandidate(clientX, clientY) {
      const el = this.$refs.canvasContainer;
      if (!el) return this.setPegCandidate(null);
      const rect = el.getBoundingClientRect();
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) {
        return this.setPegCandidate(null);
      }
      this.camera.updateMatrixWorld();
      const v = new THREE.Vector3();
      let best = null, bestD = Infinity;
      for (const pt of this._pegPoints || []) {
        v.copy(pt.world).project(this.camera);
        if (v.z > 1) continue;   // behind the camera
        const sx = rect.left + (v.x * 0.5 + 0.5) * rect.width;
        const sy = rect.top + (-v.y * 0.5 + 0.5) * rect.height;
        const d = (sx - clientX) ** 2 + (sy - clientY) ** 2;
        if (d < bestD) { bestD = d; best = pt; }
      }
      const snapPx = 70;
      this.setPegCandidate(bestD <= snapPx * snapPx ? best : null);
    },

    setPegCandidate(pt) {
      this._pegCandidate = pt;
      this.pegHasCandidate = !!pt;
      this.ensurePegHighlight();
      if (pt && this._pegHighlight) {
        this._pegHighlight.position.copy(pt.world);
        this._pegHighlight.visible = true;
        const s = this._pegRingScale || (this.modelRadius > 0 ? this.modelRadius * 0.06 : 1.5);
        this._pegHighlight.scale.set(s, s, 1);
      } else if (this._pegHighlight) {
        this._pegHighlight.visible = false;
      }
    },

    ensurePegHighlight() {
      if (this._pegHighlight) return;
      const size = 128;
      const canvas = document.createElement('canvas');
      canvas.width = canvas.height = size;
      const ctx = canvas.getContext('2d');
      const c = size / 2;
      ctx.strokeStyle = '#64ffda'; ctx.lineWidth = 10;
      ctx.beginPath(); ctx.arc(c, c, c - 12, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = 'rgba(100,255,218,0.45)'; ctx.lineWidth = 4;
      ctx.beginPath(); ctx.arc(c, c, c - 26, 0, Math.PI * 2); ctx.stroke();
      const tex = new THREE.CanvasTexture(canvas);
      tex.colorSpace = THREE.SRGBColorSpace;
      const mat = new THREE.SpriteMaterial({ map: tex, transparent: true, depthTest: false, depthWrite: false });
      this._pegHighlight = new THREE.Sprite(mat);
      this._pegHighlight.renderOrder = 1000;
      this._pegHighlight.visible = false;
      this.scene.add(this._pegHighlight);
    },

    // Signed frame image URLs for a scan (viewer .webp), for texturing the bubble faces. Pulled
    // straight from the frames client + SAS signer — the bubble only needs the images, not any
    // provenance/inference data.
    async getScanFrames(scanId) {
      const frames = await DualloFramesClient.getFramesByScanId(scanId);
      const out = [];
      await Promise.all(
        (frames ?? [])
          .filter(f => f.imagePath)
          .map(async (frame) => {
            out.push({
              stem: stemOf(frame.fileName),
              fileName: frame.fileName,
              imagePath: frame.imagePath,
              url: await organizationSasService.getSignedUrl(frame.imagePath),
            });
          })
      );
      return out;
    },

    // Signed equirect panorama URL for a capture (or null). Fetches the scan's equirect blob PATHS
    // once (cached), then signs on demand. Signing per call — not caching the signed URL — means a
    // long-open viewer re-entering a bubble never reuses an expired SAS token. No equirects → null → cube.
    async getEquirectUrl(scanId, base) {
      const blobPath = await this.getEquirectBlobPath(scanId, base);
      return blobPath ? await organizationSasService.getSignedUrl(blobPath) : null;
    },

    // The equirect blob PATH for a capture (cached per scan), or null. Shared by getEquirectUrl and the
    // re-capture thumbnail, which also needs the filename to store as the marker's source image.
    async getEquirectBlobPath(scanId, base) {
      if (!base) return null;
      this._equirectPathsByScan ||= {};
      let pathByBase = this._equirectPathsByScan[scanId];
      if (!pathByBase) {
        pathByBase = {};
        try {
          const orgId = this.organizationStore?.organizationId;
          const paths = await DualloFramesClient.getEquirectFramePaths(orgId, this.assetId, scanId);
          for (const path of paths ?? []) {
            const b = equirectBase(path);
            // The equirect endpoint returns the raw blob name with no leading slash, whereas the SAS
            // root has no trailing slash — so store a leading-slash path (matching the frame ImagePath
            // convention) or the container + asset GUID collide into an invalid blob name.
            if (b) pathByBase[b] = path.startsWith('/') ? path : `/${path}`;
          }
        } catch { /* no equirect endpoint/frames → cube fallback */ }
        this._equirectPathsByScan[scanId] = pathByBase;
      }
      return pathByBase[stripModality(base)] || null;
    },

    // Cube-face image URLs for a scan, keyed by stem (cube fallback). Blob PATHS are cached per scan;
    // the signing happens on every call so re-entering a bubble in a long-open viewer always gets a
    // fresh SAS token rather than a cached, expired one.
    async getCubeFrameUrls(scanId) {
      this._bubbleFramePaths ||= {};
      let pathByStem = this._bubbleFramePaths[scanId];
      if (!pathByStem) {
        pathByStem = {};
        try {
          const frames = await this.getScanFrames(scanId);
          for (const f of frames) if (f.stem && f.imagePath) pathByStem[f.stem] = f.imagePath;
        } catch { /* no frames → no textures */ }
        this._bubbleFramePaths[scanId] = pathByStem;
      }
      const urlByStem = {};
      await Promise.all(Object.entries(pathByStem).map(async ([stem, path]) => {
        urlByStem[stem] = await organizationSasService.getSignedUrl(path);
      }));
      return urlByStem;
    },

    // Wraps a single equirectangular image on the inside of a sphere at the capture centre — the
    // clean 360 path. NOTE: sphere orientation (up axis + yaw/north) may need a one-time tune against
    // real equirect output; the walking-path bubble uses cube faces until panoramas are produced.
    buildEquirectSphere(center, url) {
      const R = Math.min(this.modelRadius > 0 ? this.modelRadius : 5, (this.camera?.far || 500) * 0.4);
      const loader = this._texLoader || (this._texLoader = new THREE.TextureLoader());

      const grp = new THREE.Group();
      grp.name = 'photo-bubble';
      this._bubblePendingTextures = [];
      let done; this._bubblePendingTextures.push(new Promise(r => { done = r; }));
      const tex = loader.load(url, () => done(true), undefined, () => done(false));
      tex.colorSpace = THREE.SRGBColorSpace;

      const geo = new THREE.SphereGeometry(R, 64, 40);
      geo.scale(-1, 1, 1);   // flip the sphere inside-out so it's viewable from the centre (canonical
                             // equirect setup: this ALONE handles the inside view — do NOT also set
                             // side:BackSide, the two cancel out and the panorama renders invisible).
      const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.FrontSide, depthWrite: false, depthTest: false });
      const sphere = new THREE.Mesh(geo, mat);
      sphere.position.copy(center);
      // Stand the equirect upright in the Z-up world (its poles map to +Y by default → tilt +90° about X
      // so they point at world +Z).
      sphere.rotation.set(Math.PI / 2, 0, 0);
      // The equirect carries no stored world yaw, so the panorama (and anything derived from it, like
      // polygon→3D marker placement) is rotated from the PLY. BUBBLE_YAW_CALIBRATION is that azimuth
      // offset. It MUST be applied about the world vertical (Z): putting it in the Euler Z slot above
      // would, given the X-tilt and XYZ order, flip the panorama upside-down instead of spinning it.
      // Calibration confirmed 0: with the spin correctly about world Z, the equirect aligns to the PLY
      // with no extra offset (a non-zero value makes the derived cube face / marker land off by that
      // angle — Math.PI inverts it). Leave at 0 unless a capture proves to need a per-capture heading.
      const BUBBLE_YAW_CALIBRATION = 0;
      sphere.rotateOnWorldAxis(new THREE.Vector3(0, 0, 1), (this._bubbleYaw || 0) + BUBBLE_YAW_CALIBRATION);
      sphere.renderOrder = -10;
      grp.add(sphere);

      this.scene.add(grp);
      this._bubbleGroup = grp;
      this._bubbleForward = new THREE.Vector3(0, 1, 0);
    },

    // Stitch the 6 cube faces into a clean, upright cube at the capture centre, placing each face by
    // its NAME (front/up/…) rather than its per-camera world pose. We deliberately ignore the camera
    // orientation: the bubble hides the cloud and markers, so a pose-perfect alignment to the cloud
    // adds nothing for the user, while a canonical cube guarantees seamless, correctly-oriented faces
    // regardless of per-frame pose noise. (Future in-bubble annotation can convert a click to world
    // coords from the known per-frame offset — it doesn't need the faces posed to the cloud.)
    buildBubbleFaces(best, gm, urlByStem) {
      const R = Math.min(this.modelRadius > 0 ? this.modelRadius : 5, (this.camera?.far || 500) * 0.4);
      const loader = this._texLoader || (this._texLoader = new THREE.TextureLoader());
      const grp = new THREE.Group();
      grp.name = 'photo-bubble';
      // Per-face load promises — the transition holds the zoom/blur until these resolve.
      const pending = [];
      this._bubblePendingTextures = pending;

      const TARGET_HALF = Math.PI / 4;   // 45° → seamless 90° cube face

      // A 360 capture has the 6 named cube faces; a single-lens (mono) capture has a normal frame with
      // no cube suffix. Pick the render path accordingly so both work.
      const cubeFaces = best.faces.filter(h => {
        const f = cubeFaceName(h.imageName);
        return f && CUBE_FACE_LAYOUT[f];
      });

      if (cubeFaces.length) {
        // ---- 360: stitch a clean, upright cube, placing each face by NAME (not per-camera pose) ----
        // (Per-camera poses were tried for placement but are too noisy/offset on these captures — they
        // leave gaps between faces — so we keep the canonical world-axis cube.)
        grp.position.copy(best.center);
        const size = 2 * R * Math.tan(TARGET_HALF);   // square face; neighbours meet exactly at the edges
        // ONE crop shared by EVERY face → identical squares → an exactly seamless cube. Each face is trimmed
        // to its central 90° (kept fraction = 2·focal·tan45 / imageWidth). Per-face SfM focals vary by a few
        // px — enough for a hairline seam — so take the MEDIAN across the faces; one odd frame can't skew it.
        // Fall back to the "_fNN_" filename FOV, then no crop (assume 90°). Centred on the image centre:
        // these are rendered faces (principal point at the centre per the rig config), so the per-face SfM
        // optical-centre offset is noise and centring the crop on it would only shift the seam.
        const T = Math.tan(TARGET_HALF);
        const fileKeepOf = (name) => { const m = /[_-]f(\d{2,3})(?=[_.-]|$)/i.exec(name || ''); return m ? T / Math.tan(THREE.MathUtils.degToRad(Math.min(170, Math.max(60, Number(m[1]))) / 2)) : null; };
        const median = (vals) => { const a = vals.filter(v => v > 0).sort((p, q) => p - q); return a.length ? a[(a.length - 1) >> 1] : 1; };
        const keepFor = (focal, dim, name) => { const f = Number(focal), d = Number(dim); return (f > 0 && d > 0) ? (2 * f * T) / d : fileKeepOf(name); };
        const keepX = Math.min(1, Math.max(0.5, median(cubeFaces.map(h => keepFor(h.cameraFocalX ?? h.focalX, h.imageWidth, h.imageName)))));
        const keepY = Math.min(1, Math.max(0.5, median(cubeFaces.map(h => keepFor(h.cameraFocalY ?? h.focalY, h.imageHeight, h.imageName)))));
        const offX = (1 - keepX) / 2, offY = (1 - keepY) / 2;
        // Radial lens-undistort baked into the face UVs (below) to straighten the slight fisheye that
        // leaves a hairline seam where faces meet. k < 0 corrects barrel/fisheye; k > 0 is pincushion.
        // Live-tune from the console with window.setCubeK(k), then re-enter the bubble; once dialled in we
        // hardcode DEFAULT_CUBE_UNDISTORT_K and drop the setter.
        const SUBDIV = 48;
        const undistortK = Number.isFinite(this._cubeUndistortK) ? this._cubeUndistortK : DEFAULT_CUBE_UNDISTORT_K;
        for (const h of cubeFaces) {
          const url = urlByStem[stemOf(h.imageName)];
          if (!url) continue;
          const layout = CUBE_FACE_LAYOUT[cubeFaceName(h.imageName)];

          // Orient the plane so its textured (+Z) side faces inward toward the centre, with the given
          // in-plane up. Basis columns [x, y, n] form a right-handed rotation (x × y = n).
          const dir = new THREE.Vector3(...layout.dir);
          const n = dir.clone().negate();                                   // inward normal = plane +Z
          const x = new THREE.Vector3().crossVectors(new THREE.Vector3(...layout.up), n).normalize();
          const y = new THREE.Vector3().crossVectors(n, x).normalize();
          const quat = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, n));

          let done; pending.push(new Promise(r => { done = r; }));
          const tex = loader.load(url, () => done(true), undefined, () => done(false));
          tex.colorSpace = THREE.SRGBColorSpace;   // crop + undistort are baked into the mesh UVs below

          // Subdivided plane whose per-vertex UVs bake in BOTH the shared central-90° crop and a radial
          // undistort, so the texture is sampled "straightened" and neighbours meet cleanly — no shader.
          const geo = new THREE.PlaneGeometry(size, size, SUBDIV, SUBDIV);
          const uv = geo.attributes.uv;
          const cu = offX + keepX / 2, cv = offY + keepY / 2;   // crop centre in full-texture UV
          for (let i = 0; i < uv.count; i++) {
            const px = (uv.getX(i) - 0.5) * 2, py = (uv.getY(i) - 0.5) * 2;   // [-1,1] across the face
            const d = 1 + undistortK * (px * px + py * py);
            uv.setXY(i, cu + (layout.mirror ? -px : px) * d * (keepX / 2), cv + py * d * (keepY / 2));
          }
          uv.needsUpdate = true;

          const mat = new THREE.MeshBasicMaterial({ map: tex, side: THREE.DoubleSide, depthWrite: false, depthTest: false });
          const plane = new THREE.Mesh(geo, mat);
          plane.quaternion.copy(quat);
          plane.position.copy(dir).multiplyScalar(R);   // local to the group (already at centre)
          plane.renderOrder = -10;   // behind the markers so they overlay
          grp.add(plane);
        }
        // Face "front" (+Y) initially so the look-around starts on a real face, not a seam.
        this._bubbleForward = new THREE.Vector3(0, 1, 0);
      } else {
        // ---- Single-lens (mono): show the frame on a plane oriented by its camera pose (like a photo
        // hung where the camera looked), sized to the frame's true field of view. ----
        const groupQuat = new THREE.Quaternion().setFromRotationMatrix(new THREE.Matrix4().extractRotation(gm));
        let first = true;
        for (const h of best.faces) {
          const url = urlByStem[stemOf(h.imageName)];
          if (!url) continue;

          const rot = h.transform?.rotation ?? {};
          const euler = new THREE.Euler(
            THREE.MathUtils.degToRad(Number(rot.x) || 0),
            THREE.MathUtils.degToRad(Number(rot.y) || 0),
            THREE.MathUtils.degToRad(Number(rot.z) || 0),
            rot.order || 'XYZ');
          const quat = groupQuat.clone().multiply(new THREE.Quaternion().setFromEuler(euler));
          const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(quat);   // camera looks down −Z

          // Height from the frame's vertical FOV (fall back to 90° if intrinsics are missing). The
          // WIDTH is set from the image's own aspect ratio once it loads, so the frame keeps its
          // native shape — never forced into a square/cube-face footprint.
          const imgW = Number(h.imageWidth), imgH = Number(h.imageHeight);
          const focalY = Number(h.cameraFocalY ?? h.focalY);
          const ppY = Number(h.cameraOpticalCenterY ?? h.opticCenterY);
          const halfV = focalY && ppY ? Math.atan(ppY / focalY) : focalY && imgH ? Math.atan(imgH / (2 * focalY)) : TARGET_HALF;
          const height = 2 * R * Math.tan(halfV);
          const guessAsp = (imgW && imgH) ? imgW / imgH : 16 / 9;

          const mat = new THREE.MeshBasicMaterial({ map: null, side: THREE.DoubleSide, depthWrite: false, depthTest: false });
          const plane = new THREE.Mesh(new THREE.PlaneGeometry(height * guessAsp, height), mat);
          let done; pending.push(new Promise(r => { done = r; }));
          const tex = loader.load(url, (t) => {
            // Correct the width to the true image aspect (no distortion, no forced square).
            const asp = (t.image?.width && t.image?.height) ? t.image.width / t.image.height : guessAsp;
            plane.scale.x = asp / guessAsp;
            done(true);
          }, undefined, () => done(false));
          tex.colorSpace = THREE.SRGBColorSpace;
          mat.map = tex;
          plane.quaternion.copy(quat);
          plane.position.copy(best.center).addScaledVector(forward, R);
          plane.renderOrder = -10;
          grp.add(plane);
          if (first) { this._bubbleForward = forward.clone(); first = false; }
        }
      }

      if (!grp.children.length) return;
      this.scene.add(grp);
      this._bubbleGroup = grp;
      if (!this._bubbleForward) this._bubbleForward = new THREE.Vector3(0, 1, 0);
    },

    exitBubble() {
      // Bump the epoch so any in-flight openBubbleAt (mid-await) won't add its planes after we've exited.
      this._bubbleEpoch = (this._bubbleEpoch || 0) + 1;
      // Tear down the annotation tool if it was armed (restores look-around).
      if (this.bubbleDrawActive || this.bubbleDrawPoints.length) this.cancelBubbleDraw();
      this._reimageMarker = null;   // leaving street view cancels any pending re-image
      if (this._bubbleGroup) {
        this.scene?.remove(this._bubbleGroup);
        this._bubbleGroup.traverse(o => { o.geometry?.dispose?.(); o.material?.map?.dispose?.(); o.material?.dispose?.(); });
        this._bubbleGroup = null;
      }
      this._bubbleCaptures = null;
      this._bubbleIndex = -1;
      this._bubbleScanId = null;
      this._reimageMarker = null;
      this.bubbleCaptureMode = false;
      this.bubbleCaptureCount = 0;
      this.bubbleCaptureIndex = -1;
      this.bubbleCaptureInfo = [];
      this.bubbleFlatUrl = null;
      this.bubbleFlatName = null;
      this.flatZoom = 1; this.flatPanX = 0; this.flatPanY = 0; this.flatDragging = false;
      const s = this._bubbleSaved;
      if (s) {
        for (const [pc, vis] of s.cloud) pc.visible = vis;
        for (const [p, vis] of s.paths || []) p.visible = vis;
        for (const [mg, vis] of s.markers || []) mg.visible = vis;
        this.camera.position.copy(s.pos);
        this.camera.quaternion.copy(s.quat);
        this.orbitControls.target.copy(s.target);
        this.orbitControls.enablePan = s.enablePan;
        this.orbitControls.enableZoom = s.enableZoom;
        this.orbitControls.minDistance = s.minDistance;
        if (s.rotateSpeed != null) this.orbitControls.rotateSpeed = s.rotateSpeed;
        if (s.fov != null) { this.camera.fov = s.fov; this.camera.updateProjectionMatrix(); }
        this._bubbleSaved = null;
      }
      this.bubbleActive = false;
      this.emitPlaybackState();
    },

    // Any other viewer action (playback, locating a marker, slicing, isolating a zone, transforming,
    // …) must happen back in the 3D scene, not inside the photo — so drop out of the bubble first.
    // Instant exit (no zoom-out transition) so the follow-up action isn't delayed.
    exitBubbleForAction() {
      if (this.bubbleActive || this.bubbleTransition) {
        this.bubbleTransition = '';
        this.exitBubble();
      }
    },
    // The bubble state the parent's toolbar needs (it owns the UI; the child does the drawing).
    emitBubbleState() {
      this.$emit('onBubbleState', {
        active: !!this.bubbleActive,
        captureMode: !!this.bubbleCaptureMode,
        drawActive: !!this.bubbleDrawActive,
        captureCount: this.bubbleCaptureCount,
        captureIndex: this.bubbleCaptureIndex,
        captures: this.bubbleCaptureInfo,                                   // quick-hop menu entries
        captureLabel: this.bubbleCaptureInfo[this.bubbleCaptureIndex]?.label || '',
        drawPoints: this.bubbleDrawPoints.length,
      });
    },

    // ───────── In-bubble polygon annotation (street-view marker creation) ─────────
    startBubbleDraw() {
      if (!this.bubbleActive) return;
      this.bubbleDrawActive = true;
      this.bubbleDrawPoints = [];
      if (this.orbitControls) this.orbitControls.enableRotate = false;   // freeze look-around while drawing
      // Warm the visibility index now (cached) so Create resolves instantly.
      this.ensureVisibilityIndex(this._bubbleScanId);
    },
    cancelBubbleDraw() {
      this.bubbleDrawActive = false;
      this.bubbleDrawPoints = [];
      if (this.orbitControls) this.orbitControls.enableRotate = true;
    },
    clearBubbleDraw() {
      this.bubbleDrawPoints = [];
    },
    onBubbleDrawClick(e) {
      // A click that began on a vertex (grab/drag) must not also drop a new point.
      if (this._suppressDrawClick) { this._suppressDrawClick = false; return; }
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      this.bubbleDrawPoints.push({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    },
    undoBubbleDrawPoint() {
      this.bubbleDrawPoints.pop();
    },
    // Grab an existing polygon vertex to reposition it before creating. Pointer events on the draw
    // layer drive the move; the click that follows is swallowed so it doesn't add a stray point.
    startVertexDrag(i, e) {
      this.draggingVertex = i;
      this._suppressDrawClick = true;
      try { e.target.setPointerCapture?.(e.pointerId); } catch { /* not all targets support capture */ }
    },
    onVertexDragMove(e) {
      if (this.draggingVertex == null) return;
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
      const y = Math.min(Math.max(e.clientY - rect.top, 0), rect.height);
      this.bubbleDrawPoints.splice(this.draggingVertex, 1, { x, y });   // reactive replace
    },
    endVertexDrag() {
      this.draggingVertex = null;
    },
    // Create a marker from the drawn polygon: resolve its 3D position from the covered cloud points,
    // capture a square crop for the thumbnail, hand both to the parent to run the normal create flow.
    async createFromBubblePolygon() {
      if (this.bubbleDrawPoints.length < 3) return;
      const poly = this.bubbleDrawPoints.map(p => ({ ...p }));

      // Re-image mode (image-only): don't create a marker and don't require a cloud surface — we're not
      // placing a 3D point, just re-capturing the image. Use the marker's OWN position for the depth the
      // face projection needs (face cameras are at the capture centre, so depth barely matters anyway).
      if (this._reimageMarker) {
        const marker = this._reimageMarker;
        this._reimageMarker = null;

        // Flat (mono) capture: the box is a direct pixel crop of the single photo — no 3D at all.
        if (this.bubbleFlatUrl) {
          const flat = await this.buildFlatReimage(poly).catch(() => null);
          this.$emit('onBubbleReimage', {
            marker,
            imageDataUrl: flat?.imageDataUrl || this.captureBubblePolygonCrop(poly),
            provenance: flat?.provenance || null,
          });
          this.cancelBubbleDraw();
          return;
        }

        const entry = this.scanGroups?.get(marker.scanId);
        let markerWorld = null;
        if (entry) {
          entry.group.updateMatrixWorld(true);
          const p = marker.transform?.position ?? {};
          markerWorld = new THREE.Vector3(p.x || 0, p.y || 0, p.z || 0).applyMatrix4(entry.group.matrixWorld);
        }
        // Prefer the seamless equirect: the drawn region maps straight to panorama pixels, so there's no
        // cube-face to select (and no seam/edge case that can fail), and it doesn't need the marker's 3D
        // position. Fall back to the cube face only for captures with no equirect (mono / single-lens).
        let result = await this.buildEquirectReimage(poly, marker.scanId).catch(() => null);
        if (!result?.provenance) {
          const provenance = markerWorld ? this.buildBubblePolygonProvenance(poly, marker.scanId, markerWorld) : null;
          // Cube bubble (no equirect here): render the thumbnail from the stitched faces — seamless across
          // faces, exact box, no equirect distortion. Provenance (single face + bbox) still rides along for
          // the findings overlay. Fall back to the single-face crop, then an on-screen screenshot.
          let imageDataUrl = this.renderCubeBoxThumbnail(poly);
          if (!imageDataUrl && provenance) imageDataUrl = await this.buildCubeFaceThumbnail(marker.scanId, provenance).catch(() => null);
          if (!imageDataUrl) imageDataUrl = this.captureBubblePolygonCrop(poly);
          result = { provenance, imageDataUrl };
        }
        this.$emit('onBubbleReimage', { marker, imageDataUrl: result.imageDataUrl, provenance: result.provenance });
        this.cancelBubbleDraw();
        return;
      }

      // Normal create: resolve a 3D point and require a real surface under the shape.
      const vis = await this.ensureVisibilityIndex(this._bubbleScanId).catch(() => null);
      const resolved = this.resolveBubblePolygonPoint(poly, vis);
      if (!resolved) {
        this.$emit('onBubbleAnnotationError', 'Couldn’t find a surface inside the shape — draw over the structure, not the sky.');
        return;
      }
      // Provenance: which cube face the drawn shape lands on + its bbox in that face's pixels, so a
      // manual marker carries the same traceability as an AI one (findings viewer + training export).
      const provenance = this.buildBubblePolygonProvenance(poly, resolved.scanId, resolved.world);
      // Cube (legacy) bubble: render the thumbnail from the stitched faces — seamless across faces, exact
      // box, no equirect distortion. Equirect bubbles keep the single cube-face crop (unchanged). Provenance
      // still carries the single-face bbox for the findings overlay. Falls back to a screenshot.
      let imageDataUrl = this._bubbleIsEquirect ? null : this.renderCubeBoxThumbnail(poly);
      if (!imageDataUrl && provenance) {
        imageDataUrl = await this.buildCubeFaceThumbnail(resolved.scanId, provenance).catch(() => null);
      }
      if (!imageDataUrl) imageDataUrl = this.captureBubblePolygonCrop(poly);

      this.$emit('onBubbleAnnotation', {
        point: resolved.world,
        localPoint: resolved.local,
        scanId: resolved.scanId,
        inspectionAreaId: resolved.inspectionAreaId ?? null,
        imageDataUrl,
        provenance,
      });
      this.cancelBubbleDraw();
    },
    // Re-image an EXISTING marker: drop straight into the nearest capture, aim at the marker, and arm
    // re-image mode so the next drawn box updates THIS marker (onBubbleReimage) instead of creating a
    // new one. Returns false when the scan has no captures (nothing to enter). User can still navigate.
    async startBubbleReimage(marker) {
      if (!marker?.scanId) return false;
      const entry = this.scanGroups?.get(marker.scanId);
      const caps = this.orderedCapturesForScan(marker.scanId);
      if (!entry || !caps?.length) return false;

      entry.group.updateMatrixWorld(true);
      const p = marker.transform?.position ?? {};
      const markerWorld = new THREE.Vector3(p.x || 0, p.y || 0, p.z || 0).applyMatrix4(entry.group.matrixWorld);

      let best = null, bestD = Infinity;
      for (const c of caps) { const d = c.world.distanceTo(markerWorld); if (d < bestD) { bestD = d; best = c; } }
      if (!best) return false;

      this._reimageMarker = marker;
      this.bubbleCaptureMode = true;   // show the "Create thumbnail" draw controls
      await this.openBubbleAt(marker.scanId, best.faces, best.world, best.base);
      // Aim from the capture centre toward the marker so it's in view on arrival.
      const dir = markerWorld.clone().sub(best.world);
      if (dir.lengthSq() > 1e-9 && this.orbitControls) {
        this.orbitControls.target.copy(best.world.clone().addScaledVector(dir.normalize(), Math.max(this.modelRadius * 0.001, 1e-3)));
      }
      return true;
    },
    cancelBubbleReimage() {
      this._reimageMarker = null;
      this.bubbleCaptureMode = false;
    },
    // --- Flat (mono) capture: pan/zoom + draw-to-pixel crop ------------------
    applyFlatZoom(factor, ax, ay) {
      const next = Math.min(Math.max(this.flatZoom * factor, 1), 8);
      if (next === this.flatZoom) return;
      const el = this.$refs.bubbleFlatImg?.parentElement;
      const rect = el ? el.getBoundingClientRect() : null;
      const cx = rect ? rect.left + rect.width / 2 : 0;
      const cy = rect ? rect.top + rect.height / 2 : 0;
      const ux = (ax ?? cx) - cx, uy = (ay ?? cy) - cy;
      const ratio = next / this.flatZoom;
      this.flatPanX = ux * (1 - ratio) + this.flatPanX * ratio;
      this.flatPanY = uy * (1 - ratio) + this.flatPanY * ratio;
      this.flatZoom = next;
      if (this.flatZoom <= 1.001) { this.flatZoom = 1; this.flatPanX = 0; this.flatPanY = 0; }
    },
    onFlatWheel(e) { this.applyFlatZoom(e.deltaY < 0 ? 1.2 : 1 / 1.2, e.clientX, e.clientY); },
    onFlatDragStart(e) {
      if (this.flatZoom <= 1 || e.button !== 0) return;
      this.flatDragging = true; this.flatDragLastX = e.clientX; this.flatDragLastY = e.clientY;
    },
    onFlatDragMove(e) {
      if (!this.flatDragging) return;
      this.flatPanX += e.clientX - this.flatDragLastX;
      this.flatPanY += e.clientY - this.flatDragLastY;
      this.flatDragLastX = e.clientX; this.flatDragLastY = e.clientY;
    },
    onFlatDragEnd() { this.flatDragging = false; },
    // Turn the drawn polygon (screen px) into a thumbnail + provenance straight from the flat image's
    // pixels — maps each point through the <img>'s on-screen rect (which already bakes in the pan/zoom
    // and the object-fit letterboxing). Returns { provenance, imageDataUrl } or null.
    async buildFlatReimage(poly) {
      const imgEl = this.$refs.bubbleFlatImg;
      if (!imgEl || !this.bubbleFlatName) return null;
      const W = imgEl.naturalWidth, H = imgEl.naturalHeight;
      const imgRect = imgEl.getBoundingClientRect();
      if (!W || !H || !imgRect.width || !imgRect.height) return null;
      // Draw points are relative to the canvas container; the image rect is absolute — bridge with the
      // container's own offset.
      const base = this.$refs.canvasContainer.getBoundingClientRect();

      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of poly) {
        const px = ((base.left + p.x - imgRect.left) / imgRect.width) * W;
        const py = ((base.top + p.y - imgRect.top) / imgRect.height) * H;
        if (px < minX) minX = px; if (py < minY) minY = py;
        if (px > maxX) maxX = px; if (py > maxY) maxY = py;
      }
      minX = Math.max(0, Math.min(minX, W)); maxX = Math.max(0, Math.min(maxX, W));
      minY = Math.max(0, Math.min(minY, H)); maxY = Math.max(0, Math.min(maxY, H));
      const bw = maxX - minX, bh = maxY - minY;
      if (bw < 1 || bh < 1) return null;

      // Square thumbnail crop around the detection (same feel as the cube/equirect thumbnails).
      let side = Math.ceil(Math.max(Math.max(bw, bh) * 1.3, 250));
      side = Math.min(side, Math.min(W, H));
      const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
      const left = Math.min(Math.max(Math.round(cx - side / 2), 0), W - side);
      const top = Math.min(Math.max(Math.round(cy - side / 2), 0), H - side);
      const out = 512;
      const canvas = document.createElement('canvas');
      canvas.width = out; canvas.height = out;
      canvas.getContext('2d').drawImage(imgEl, left, top, side, side, 0, 0, out, out);

      return {
        imageDataUrl: canvas.toDataURL('image/webp', 0.9),
        provenance: {
          sourceImageFileName: this.bubbleFlatName,
          bboxX: cx, bboxY: cy, bboxWidth: bw, bboxHeight: bh,   // centre-origin, image pixels
        },
      };
    },
    // The drawn polygon's vertices (+ centroid, appended last) as unit WORLD ray directions from the
    // bubble camera. The camera sits AT the capture centre, so these rays ARE the panorama directions —
    // no 3D depth/position needed, which is what the equirect box wants (robust even if the marker has a
    // weak position, e.g. one with no image yet).
    bubblePolygonRayDirections(poly) {
      const cam = this.camera;
      cam.updateMatrixWorld();
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
      const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
      return [...poly, { x: cx, y: cy }].map((p) => {
        this.mouse.x = (p.x / rect.width) * 2 - 1;
        this.mouse.y = -(p.y / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouse, cam);
        return this.raycaster.ray.direction.clone().normalize();
      });
    },
    // The drawn polygon's vertices (+ centroid, appended last) as WORLD points at the surface depth.
    bubblePolygonWorldPoints(poly, resolvedWorld) {
      const cam = this.camera;
      cam.updateMatrixWorld();
      const camPos = cam.position;
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      const depth = camPos.distanceTo(resolvedWorld);
      const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
      const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
      return [...poly, { x: cx, y: cy }].map((p) => {
        this.mouse.x = (p.x / rect.width) * 2 - 1;
        this.mouse.y = -(p.y / rect.height) * 2 + 1;
        this.raycaster.setFromCamera(this.mouse, cam);
        const dir = this.raycaster.ray.direction.clone().normalize();
        return camPos.clone().addScaledVector(dir, depth);
      });
    },
    // Re-capture via the seamless equirect panorama: map the drawn region straight to panorama pixels
    // (no cube face to pick), crop the thumbnail from the equirect, and store the equirect as the marker's
    // source image. Returns { provenance, imageDataUrl } or null when the capture has no equirect.
    async buildEquirectReimage(poly, scanId) {
      const caps = this._bubbleCaptures || [];
      const cap = (this._bubbleIndex >= 0) ? caps[this._bubbleIndex] : null;
      if (!cap?.world) return null;

      const blobPath = await this.getEquirectBlobPath(scanId, cap.base);
      if (!blobPath) return null;   // mono / single-lens capture → caller falls back to the cube face

      // Box straight from the drawn ray directions (origin = camera at the capture centre) — no dependency
      // on the marker's 3D position, so "Add image" (no prior placement) behaves like "Change image".
      const dirs = this.bubblePolygonRayDirections(poly);
      const uv = equirectBoxFromWorldPoints(new THREE.Vector3(0, 0, 0), dirs);
      if (!uv) return null;

      const url = await organizationSasService.getSignedUrl(blobPath);
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = url;
      });
      const W = img.naturalWidth, H = img.naturalHeight;
      if (!W || !H) return null;

      // Centre-origin box in equirect pixels.
      const bboxX = (uv.left + uv.width / 2) * W;
      const bboxY = (uv.top + uv.height / 2) * H;
      const bboxWidth = uv.width * W;
      const bboxHeight = uv.height * H;

      // Square thumbnail crop around the detection (mirrors the cube-face/DetectionCropper feel). The
      // equirect wraps horizontally, so draw in two slices when the crop crosses the ±180° seam.
      let side = Math.ceil(Math.max(Math.max(bboxWidth, bboxHeight) * 1.3, 250));
      side = Math.min(side, H);
      const top = Math.min(Math.max(Math.round(bboxY - side / 2), 0), H - side);
      let left = Math.round(bboxX - side / 2);
      left = ((left % W) + W) % W;
      const out = 512;
      const canvas = document.createElement('canvas');
      canvas.width = out; canvas.height = out;
      const ctx = canvas.getContext('2d');
      const first = Math.min(side, W - left);
      ctx.drawImage(img, left, top, first, side, 0, 0, (first / side) * out, out);
      if (first < side) {
        ctx.drawImage(img, 0, top, side - first, side, (first / side) * out, 0, ((side - first) / side) * out, out);
      }
      const imageDataUrl = canvas.toDataURL('image/webp', 0.9);

      return {
        imageDataUrl,
        provenance: {
          sourceImageFileName: blobPath.split(/[\\/]/).pop(),   // equirect file → overlay shows the pano
          bboxX, bboxY, bboxWidth, bboxHeight,
        },
      };
    },
    // Projects the drawn polygon onto the current capture's cube faces and returns the face the shape
    // lands on MOST, with the shape's bbox in that face's pixels (centre-origin, like a Roboflow box).
    // Used as the fallback for captures with no equirect (mono / single-lens).
    // Legacy cube bubble: pick the face the stitched cube RENDERS toward a world direction (max dot with
    // the CUBE_FACE_LAYOUT face direction), returning its label + world frame {dir, up} so provenance is
    // projected onto the same face the user drew on. The equirect path uses faceLabelForWorldDir instead.
    // Pick the cube face (from those the capture actually HAS) that holds the most of the drawn box by
    // AREA — project the polygon onto each candidate face and keep the one with the largest in-bounds
    // footprint. Beats centroid-direction when a box straddles a seam (it attaches to the face it mostly
    // lands on). Returns { label, dir, up } in world space, or null. Restricted to availableLabels so a
    // legacy capture missing a side face falls back to the nearest present one.
    bestCubeFaceForBox(center, worldPts, availableLabels = null) {
      const verts = worldPts.slice(0, -1);   // drop the trailing centroid
      if (verts.length < 2) return null;
      let best = null, bestArea = -Infinity;
      for (const [label, layout] of Object.entries(CUBE_FACE_LAYOUT)) {
        if (availableLabels && !availableLabels.has(label)) continue;
        const dir = new THREE.Vector3(layout.dir[0], layout.dir[1], layout.dir[2]);
        const up = new THREE.Vector3(layout.up[0], layout.up[1], layout.up[2]);
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity, inFront = 0;
        for (const p of verts) {
          const uv = projectWorldToFaceDir(center, dir, up, p);
          if (!uv) continue;               // behind this face
          inFront++;
          if (uv.x < minX) minX = uv.x; if (uv.y < minY) minY = uv.y;
          if (uv.x > maxX) maxX = uv.x; if (uv.y > maxY) maxY = uv.y;
        }
        if (inFront < 2) continue;         // not enough of the box is in front of this face
        const area = Math.max(0, maxX - minX) * Math.max(0, maxY - minY);   // uv already clamped to [0,1]
        if (area > bestArea) { bestArea = area; best = { label, dir, up }; }
      }
      return best;
    },
    buildBubblePolygonProvenance(poly, scanId, resolvedWorld) {
      try {
        const caps = this._bubbleCaptures || [];
        const i = this._bubbleIndex ?? -1;
        const cap = (i >= 0 && caps[i]) ? caps[i] : null;
        const faces = cap?.faces;
        if (!faces || !faces.length || !resolvedWorld || !cap.world) return null;

        const center = cap.world.clone();   // capture (rig) centre, WORLD
        // Polygon vertices (+ centroid, last) → WORLD points. We work in world space and the equirect's
        // world frame, NOT the camera-helper poses (those are yaw-rotated from the cube-face images).
        const worldPts = this.bubblePolygonWorldPoints(poly, resolvedWorld);

        // The face is the one whose IMAGE shows the detection's world direction (from the rig centre).
        // Which frame that direction lives in depends on the bubble the user drew on: the equirect sphere
        // is world-aligned (FACE_RING), while the stitched legacy cube uses CUBE_FACE_LAYOUT. Selection
        // AND the per-vertex projection below must share that frame — otherwise the right face still crops
        // the wrong spot. (Legacy cube captures went through the equirect frame and landed on the wrong
        // face; this branch picks the face the cube actually rendered.)
        const centroidWorld = worldPts[worldPts.length - 1];
        const centroidDir = centroidWorld.clone().sub(center);
        const availableFaceLabels = new Set(faces.map(f => cubeFaceName(f.imageName)).filter(Boolean));
        // Cube bubble: attach to the present face holding the MOST of the box by area (handles seam
        // straddles). Equirect bubble keeps the world-aligned ring.
        const cubeFrame = this._bubbleIsEquirect ? null : this.bestCubeFaceForBox(center, worldPts, availableFaceLabels);
        const faceLabel = cubeFrame ? cubeFrame.label : faceLabelForWorldDir(centroidDir);

        // Find the capture's actual frame file for that face label, and its pixel size.
        const faceHelper = faces.find(f => cubeFaceName(f.imageName) === faceLabel);
        if (!faceHelper?.imageName) return null;
        const W = faceHelper.imageWidth || 0, H = faceHelper.imageHeight || 0;
        if (!W || !H) return null;

        // bbox from the polygon vertices projected onto the canonical face camera (NOT the centroid),
        // in whichever frame selected the face.
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        for (let k = 0; k < worldPts.length - 1; k++) {
          const uv = cubeFrame
            ? projectWorldToFaceDir(center, cubeFrame.dir, cubeFrame.up, worldPts[k])
            : projectWorldToFace(center, faceLabel, worldPts[k]);
          if (!uv) continue;
          const px = uv.x * W, py = uv.y * H;
          if (px < minX) minX = px; if (py < minY) minY = py;
          if (px > maxX) maxX = px; if (py > maxY) maxY = py;
        }
        if (!isFinite(minX)) return null;
        minX = Math.max(0, minX); minY = Math.max(0, minY);
        maxX = Math.min(W, maxX); maxY = Math.min(H, maxY);
        if (maxX - minX < 1 || maxY - minY < 1) return null;

        return {
          sourceImageFileName: faceHelper.imageName,
          bboxX: (minX + maxX) / 2,     // centre-origin, matching the Roboflow bbox convention
          bboxY: (minY + maxY) / 2,
          bboxWidth: maxX - minX,
          bboxHeight: maxY - minY,
        };
      } catch {
        return null;   // provenance is best-effort; never block marker creation
      }
    },
    // Crops the marker thumbnail from the cube-face frame using the SAME square the findings viewer
    // outlines (DetectionCropper: max(w,h)*1.3, floored at 250, centred, slid inside the frame), so the
    // thumbnail and the full-image / street-view box frame exactly the same region.
    async buildCubeFaceThumbnail(scanId, provenance) {
      const urlByStem = await this.getCubeFrameUrls(scanId);
      const url = urlByStem[stemOf(provenance.sourceImageFileName)];
      if (!url) return null;
      const img = await new Promise((resolve, reject) => {
        const im = new Image();
        im.crossOrigin = 'anonymous';
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = url;
      });
      const W = img.naturalWidth, H = img.naturalHeight;
      let side = Math.ceil(Math.max(Math.max(provenance.bboxWidth, provenance.bboxHeight) * 1.3, 250));
      side = Math.min(side, Math.min(W, H));
      if (side <= 0) return null;
      const left = Math.min(Math.max(Math.round(provenance.bboxX - side / 2), 0), W - side);
      const top = Math.min(Math.max(Math.round(provenance.bboxY - side / 2), 0), H - side);
      const out = 512;
      const canvas = document.createElement('canvas');
      canvas.width = out; canvas.height = out;
      canvas.getContext('2d').drawImage(img, left, top, side, side, 0, 0, out, out);
      return canvas.toDataURL('image/webp', 0.9);
    },
    // Map the polygon to a 3D position. Preferred path (when the visibility index is available): take
    // the exact dense-cloud points the current capture camera and its neighbours SEE, keep the ones
    // that fall inside the drawn polygon, and place the marker at their mean — those are real surface
    // points the cameras are responsible for, so no sky / see-through background sneaks in. If the
    // index is absent (or doesn't line up with the loaded cloud), fall back to projecting the whole
    // cloud and taking the nearest dense cluster's depth along the polygon-centroid ray.
    resolveBubblePolygonPoint(poly, vis) {
      const scanId = this._bubbleScanId;
      const entry = scanId ? this.scanGroups?.get(scanId) : null;
      if (!entry?.pointCloud) return null;
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      const cam = this.camera;
      cam.updateMatrixWorld();

      const pos = entry.pointCloud.geometry.attributes.position;
      if (!pos) return null;
      entry.group.updateMatrixWorld(true);
      const gm = entry.group.matrixWorld;
      const inv = cam.matrixWorldInverse;

      // Centroid of the polygon (screen) → view-ray direction (used by the fallback + last-resort BVH).
      const cx = poly.reduce((s, p) => s + p.x, 0) / poly.length;
      const cy = poly.reduce((s, p) => s + p.y, 0) / poly.length;
      this.mouse.x = (cx / rect.width) * 2 - 1;
      this.mouse.y = -(cy / rect.height) * 2 + 1;
      this.raycaster.setFromCamera(this.mouse, cam);
      const dir = this.raycaster.ray.direction.clone().normalize();

      const finish = (world) => {
        const local = this.toScanLocal(scanId, world);
        let inspectionAreaId = null;
        try { inspectionAreaId = this.markerGeometry?.findInspectionAreaForPoint?.(local, scanId) ?? null; } catch { /* optional */ }
        return { world, local, scanId, inspectionAreaId };
      };

      // Preferred: mean of the visible surface points inside the polygon.
      const meanWorld = this.resolveFromVisibility(poly, vis, pos, gm, cam, inv, rect);
      if (meanWorld) return finish(meanWorld);

      // Fallback: nearest dense cluster depth along the centroid ray.
      const camPos = cam.position;
      const v = new THREE.Vector3();
      const step = Math.max(1, Math.floor(pos.count / 60000));   // sample huge clouds for a one-off click
      const depths = [];
      for (let i = 0; i < pos.count; i += step) {
        v.set(pos.getX(i), pos.getY(i), pos.getZ(i)).applyMatrix4(gm);
        // Behind the camera? (camera looks down -z in view space) — skip.
        const camZ = v.x * inv.elements[2] + v.y * inv.elements[6] + v.z * inv.elements[10] + inv.elements[14];
        if (camZ >= 0) continue;
        const ndc = v.clone().project(cam);
        const sx = (ndc.x * 0.5 + 0.5) * rect.width;
        const sy = (-ndc.y * 0.5 + 0.5) * rect.height;
        if (!this.pointInPolygon(sx, sy, poly)) continue;
        depths.push(camPos.distanceTo(v));
      }

      let depth = this.clusterDepth(depths);
      if (depth == null) {
        // No covered points — last resort: BVH raycast of the centroid ray. The cloud is hidden in the
        // bubble (findClosestScanPoint skips hidden scans), so flip visibility just for the test.
        const wasVisible = entry.group.visible;
        entry.group.visible = true;
        const hit = this.findClosestScanPoint(this.raycaster.ray);
        entry.group.visible = wasVisible;
        if (!hit) return null;
        depth = camPos.distanceTo(hit.worldPoint);
      }

      return finish(camPos.clone().addScaledVector(dir, depth));
    },

    // Visibility-index placement: union the dense-cloud points the current capture (+ up to
    // VIS_NEIGHBOURS either side along the walk) see, project them through the aligned bubble camera,
    // keep those inside the polygon, and return the mean world position. Null if no index, no hits, or
    // the index references points outside the loaded cloud (a decimated bake won't line up → fall back).
    resolveFromVisibility(poly, vis, pos, gm, cam, inv, rect) {
      if (!vis) return null;
      const caps = this._bubbleCaptures || [];
      const i = this._bubbleIndex ?? -1;
      if (i < 0 || !caps.length) return null;

      const VIS_NEIGHBOURS = 3;   // "2/3 cameras left and right of the one we're in"
      const faceNames = [];
      for (let k = Math.max(0, i - VIS_NEIGHBOURS); k <= Math.min(caps.length - 1, i + VIS_NEIGHBOURS); k++) {
        for (const h of caps[k].faces || []) if (h?.imageName) faceNames.push(h.imageName);
      }
      const indices = this.visIndicesForFaceNames(vis, faceNames);
      if (!indices.size) return null;

      const count = pos.count;
      const rig = cam.position;               // street-view rig = capture centre
      const v = new THREE.Vector3();
      const kept = [];                        // visible surface points inside the bbox: { p, depth }
      let outOfRange = 0;
      for (const vi of indices) {
        if (vi < 0 || vi >= count) {
          // The index addresses the raw dense cloud; if more than a few land outside the loaded
          // geometry it isn't the same cloud (e.g. decimated) — abandon and use the projection fallback.
          if (++outOfRange > 8) return null;
          continue;
        }
        v.set(pos.getX(vi), pos.getY(vi), pos.getZ(vi)).applyMatrix4(gm);
        const camZ = v.x * inv.elements[2] + v.y * inv.elements[6] + v.z * inv.elements[10] + inv.elements[14];
        if (camZ >= 0) continue;   // behind the camera
        const ndc = v.clone().project(cam);
        const sx = (ndc.x * 0.5 + 0.5) * rect.width;
        const sy = (-ndc.y * 0.5 + 0.5) * rect.height;
        if (!this.pointInPolygon(sx, sy, poly)) continue;
        kept.push({ p: v.clone(), depth: rig.distanceTo(v) });
      }
      if (kept.length < 3) return null;   // too few visible surface points inside the shape to trust

      // The bbox cone can graze a nearer object and a farther wall; keep only the NEAREST dense cluster
      // (the flagged surface) and place the marker at the centroid of just those points.
      const win = this.nearestDenseDepthWindow(kept.map(k => k.depth));
      const near = win ? kept.filter(k => k.depth >= win.lo && k.depth <= win.hi) : kept;
      const use = near.length ? near : kept;
      const sum = new THREE.Vector3();
      for (const k of use) sum.add(k.p);
      return sum.multiplyScalar(1 / use.length);
    },

    // Fetches + parses the scan's visibility index (manifest.json + index.bin under
    // {assetId}/scans/{scanId}/model/visibility-index/). Cached per scan; a missing/unreadable index
    // caches null so we don't re-request it. Mirrors the backend VisibilityIndex.Parse.
    async ensureVisibilityIndex(scanId) {
      if (!scanId || !this.assetId) return null;
      this._visibilityByScan ||= {};
      if (scanId in this._visibilityByScan) return this._visibilityByScan[scanId];   // null = known-absent

      let parsed = null;
      try {
        const base = `/${this.assetId}/scans/${scanId}/model/visibility-index`;
        const [manifestUrl, indexUrl] = await Promise.all([
          organizationSasService.getSignedUrl(`${base}/manifest.json`),
          organizationSasService.getSignedUrl(`${base}/index.bin`),
        ]);
        const [mRes, iRes] = await Promise.all([fetch(manifestUrl), fetch(indexUrl)]);
        if (mRes.ok && iRes.ok) {
          const manifest = await mRes.json();
          const buf = await iRes.arrayBuffer();
          parsed = this.parseVisibilityIndex(manifest, buf);
        }
      } catch { /* missing/unreadable index → cache null, don't re-request */ }

      this._visibilityByScan[scanId] = parsed;
      return parsed;
    },

    // manifest.cameras[{name, byteOffset, count}] + index.bin (Uint32LE dense-cloud vertex indices).
    // Target browsers are little-endian, so a Uint32Array view over the buffer reads the file directly.
    // Cameras are keyed by exact name (== camera-helper ImageName) and by a tolerant stem for fallback.
    parseVisibilityIndex(manifest, buf) {
      const idx = new Uint32Array(buf);
      const byName = new Map();
      const byStem = new Map();
      for (const cam of manifest?.cameras ?? []) {
        const name = cam?.name;
        if (!name) continue;
        const rec = { start: Math.floor((cam.byteOffset ?? 0) / 4), count: cam.count ?? 0 };
        byName.set(name, rec);
        const stem = this.visStem(name);
        if (stem && !byStem.has(stem)) byStem.set(stem, rec);
      }
      return { idx, total: idx.length, byName, byStem };
    },

    // Union of dense-cloud vertex indices for a set of camera/face names.
    visIndicesForFaceNames(vis, faceNames) {
      const out = new Set();
      for (const raw of faceNames) {
        let rec = vis.byName.get(raw);
        if (!rec) rec = vis.byStem.get(this.visStem(raw));
        if (!rec || rec.count <= 0) continue;
        const end = Math.min(rec.start + rec.count, vis.total);
        for (let j = rec.start; j < end; j++) out.add(vis.idx[j]);
      }
      return out;
    },

    // Filename stem for tolerant camera-name matching (drop directory + extension, lowercase).
    visStem(name) {
      return (name ?? '').split(/[\\/]/).pop().replace(/\.[^.]+$/, '').toLowerCase();
    },
    // 2D point-in-polygon (ray casting).
    pointInPolygon(x, y, poly) {
      let inside = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
        if (((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi)) inside = !inside;
      }
      return inside;
    },
    // Depth of the NEAREST dense cluster of covered points — the closest real surface in the view
    // direction (the thing being flagged), not a denser wall behind it. Histogram the depths, walk
    // from near to far for the first bin that's a clear concentration (well above average density),
    // extend over the contiguous dense run, and average the depths within it.
    clusterDepth(depths) {
      const win = this.nearestDenseDepthWindow(depths);
      if (!win) return null;
      const inside = depths.filter(d => d >= win.lo && d <= win.hi);
      const arr = inside.length ? inside : depths;
      return arr.reduce((s, d) => s + d, 0) / arr.length;
    },
    // Depth window [lo, hi] of the NEAREST dense cluster in a set of distances-from-rig. Histogram the
    // depths, walk from near to far for the first bin that's a clear concentration (well above average
    // density — a real surface, not stray foreground/sky points), and extend over the contiguous dense
    // run. This is the "first group of denser points" seen from the rig; farther walls are ignored.
    nearestDenseDepthWindow(depths) {
      const n = depths.length;
      if (!n) return null;
      let min = Infinity, max = -Infinity;
      for (const d of depths) { if (d < min) min = d; if (d > max) max = d; }
      if (n < 8) return { lo: min, hi: max };
      const range = (max - min) || 1;
      const bins = Math.min(40, Math.max(8, Math.round(Math.sqrt(n))));
      const binW = range / bins;
      const counts = new Array(bins).fill(0);
      for (const d of depths) {
        let b = Math.floor((d - min) / binW);
        if (b >= bins) b = bins - 1; else if (b < 0) b = 0;
        counts[b]++;
      }
      const avg = n / bins;
      const dense = Math.max(4, avg * 2.5);   // "dense enough" to count as a surface
      // First (nearest) bin that's clearly dense; fall back to the overall densest if none stands out.
      let start = counts.findIndex(c => c >= dense);
      if (start < 0) { let p = 0; for (let i = 1; i < bins; i++) if (counts[i] > counts[p]) p = i; start = p; }
      // Extend forward across the contiguous run that stays at/above average density.
      let end = start;
      while (end + 1 < bins && counts[end + 1] >= Math.max(2, avg)) end++;
      return { lo: min + start * binW, hi: min + (end + 1) * binW };
    },
    // Square crop of the current bubble view around the polygon (+padding), sized to the larger of the
    // shape's width/height so the whole outline fits. Returns a webp data URL.
    captureBubblePolygonCrop(poly) {
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (const p of poly) { if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y; if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y; }
      const span = Math.max(maxX - minX, maxY - minY);
      const pad = 0.18 * span + 12;                 // padding grows with the shape

      // Keep the square fully INSIDE the canvas (shift inward near an edge, shrink if the shape is
      // bigger than the viewport) so the crop never samples past the edge — that was the dark border.
      let side = Math.min(span + pad * 2, rect.width, rect.height);
      const ccx = (minX + maxX) / 2, ccy = (minY + maxY) / 2;
      const srcXcss = Math.min(Math.max(ccx - side / 2, 0), rect.width - side);
      const srcYcss = Math.min(Math.max(ccy - side / 2, 0), rect.height - side);

      const gl = this.renderer.domElement;
      const scaleX = gl.width / rect.width, scaleY = gl.height / rect.height;

      this.renderer.render(this.scene, this.camera);   // ensure the buffer holds the current frame

      const out = 512;
      const canvas = document.createElement('canvas');
      canvas.width = out; canvas.height = out;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(gl, srcXcss * scaleX, srcYcss * scaleY, side * scaleX, side * scaleY, 0, 0, out, out);
      return canvas.toDataURL('image/webp', 0.9);
    },

    // Legacy cube-bubble thumbnail: render the drawn box straight from the stitched face planes through an
    // offscreen perspective camera framed exactly to the polygon. Rectilinear (no equirect pole distortion)
    // and seamless ACROSS faces (it samples whatever the faces show, including over a seam), at the faces'
    // own resolution. Returns a webp data URL, or null if it can't be framed (caller falls back).
    renderCubeBoxThumbnail(poly) {
      if (!this._bubbleGroup || !this.renderer || !poly || poly.length < 3) return null;
      const dirs = this.bubblePolygonRayDirections(poly);      // world ray dirs from the rig centre; last = centroid
      if (dirs.length < 2) return null;
      const center = this.camera.position.clone();
      const centroid = dirs[dirs.length - 1].clone().normalize();

      // Offscreen camera at the rig centre, looking along the box centroid. World-up (Z) keeps the crop
      // upright; near the poles (looking near straight up/down) Z is degenerate, so fall back to Y.
      const off = new THREE.PerspectiveCamera(60, 1, 0.001, 1000);
      off.position.copy(center);
      off.up.copy(Math.abs(centroid.z) > 0.95 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(0, 0, 1));
      off.lookAt(center.clone().add(centroid));
      off.updateMatrixWorld(true);

      // Angular half-extents of the box corners in view space → the FOV + aspect that frame the box.
      let maxH = 0, maxV = 0;
      for (let i = 0; i < dirs.length - 1; i++) {
        const v = dirs[i].clone().transformDirection(off.matrixWorldInverse);
        if (v.z >= 0) continue;                                 // behind the camera — skip
        maxH = Math.max(maxH, Math.atan2(Math.abs(v.x), -v.z));
        maxV = Math.max(maxV, Math.atan2(Math.abs(v.y), -v.z));
      }
      if (maxH <= 1e-3 || maxV <= 1e-3) return null;
      const PAD = 1.12, CAP = THREE.MathUtils.degToRad(75);     // small margin; cap FOV so a huge box stays sane
      const halfV = Math.min(maxV * PAD, CAP);
      const halfH = Math.min(maxH * PAD, CAP);
      off.fov = THREE.MathUtils.radToDeg(halfV * 2);
      off.aspect = Math.tan(halfH) / Math.tan(halfV);
      off.updateProjectionMatrix();

      const outH = 512;
      const outW = Math.max(1, Math.round(outH * off.aspect));
      const rt = new THREE.WebGLRenderTarget(outW, outH);
      rt.texture.colorSpace = THREE.SRGBColorSpace;   // encode like the display canvas; else the read-back is dark (linear)
      const prevRT = this.renderer.getRenderTarget();
      try {
        this.renderer.setRenderTarget(rt);
        this.renderer.render(this.scene, off);
        const buf = new Uint8Array(outW * outH * 4);
        this.renderer.readRenderTargetPixels(rt, 0, 0, outW, outH, buf);
        const canvas = document.createElement('canvas');
        canvas.width = outW; canvas.height = outH;
        const ctx = canvas.getContext('2d');
        const imgData = ctx.createImageData(outW, outH);
        for (let y = 0; y < outH; y++) {                        // GL buffer is bottom-up → flip into the canvas
          const src = (outH - 1 - y) * outW * 4;
          imgData.data.set(buf.subarray(src, src + outW * 4), y * outW * 4);
        }
        ctx.putImageData(imgData, 0, 0);
        return canvas.toDataURL('image/webp', 0.9);
      } catch {
        return null;
      } finally {
        this.renderer.setRenderTarget(prevRT);
        rt.dispose();
      }
    },

    // Play (fresh) / resume / pause, from a single control. First-person fly-through along the path.
    togglePlayback() {
      this.exitBubbleForAction();
      if (this.isPlaying && !this.isPaused) { this.pausePlayback(); return; }
      if (this.isPlaying && this.isPaused) {   // resume
        this.isPaused = false;
        this.orbitControls.enablePan = false;
        this._playLast = performance.now();
        this.emitPlaybackState();
        return;
      }
      // Fresh start — rebuild in case the model changed since last time.
      if (!this.buildPlaybackPath() || this._playTotal <= 0) { this.playbackAvailable = false; this.emitPlaybackState(); return; }
      this._playDistance = 0;
      this.playbackProgress = 0;
      this.isPlaying = true;
      this.isPaused = false;
      this.frameStartOfPath();
      // Pan would fight the fly-through; disable it while flying (orbit + zoom stay live).
      this.orbitControls.enablePan = false;
      this._playLast = performance.now();
      this.emitPlaybackState();
    },

    pausePlayback() {
      if (!this.isPlaying) return;
      this.isPaused = true;
      this.orbitControls.enablePan = true;
      this.emitPlaybackState();
    },

    stopPlayback() {
      this.isPlaying = false;
      this.isPaused = false;
      this._playDistance = 0;
      this.playbackProgress = 0;
      this.orbitControls.enablePan = true;
      this.emitPlaybackState();
    },

    setPlaybackSpeed(value) {
      const v = parseFloat(value);
      if (Number.isFinite(v) && v > 0) this.playbackSpeed = v;
      this.emitPlaybackState();
    },

    // Scrub to a fraction (0..1): moves the camera along the path.
    seekPlayback(fraction) {
      if (!this._playCurve && !this.buildPlaybackPath()) return;
      const f = Math.min(Math.max(fraction, 0), 1);
      this._playDistance = f * this._playTotal;
      this.playbackProgress = f;
      this.movePivotTo(this._playDistance);
      this._playLast = performance.now();
      this.emitPlaybackState();
    },

    // Advances the fly-through one frame (called from the render loop while playing, before
    // OrbitControls.update()).
    updatePlaybackFrame() {
      const now = performance.now();
      let dt = (now - (this._playLast ?? now)) / 1000;
      this._playLast = now;
      dt = Math.min(dt, 0.1);   // clamp so a background-tab stall doesn't teleport the pivot

      // Base pace: traverse the whole path in ~24s at 1×, model-scale-independent.
      const base = this._playTotal / 24;
      this._playDistance += dt * base * this.playbackSpeed;

      // Fly the camera to the end, then stop.
      if (this._playDistance >= this._playTotal) {
        this._playDistance = this._playTotal;
        this.movePivotTo(this._playDistance);
        this.isPlaying = false;
        this.isPaused = false;
        this.playbackProgress = 1;
        this.orbitControls.enablePan = true;
        this.emitPlaybackState();
        return;
      }
      this.movePivotTo(this._playDistance);
      this.playbackProgress = this._playTotal ? this._playDistance / this._playTotal : 0;
      // Throttle progress broadcasts to the tool card (~10/s) so we don't diff Vue every frame.
      if (!this._lastStateEmit || now - this._lastStateEmit > 100) {
        this._lastStateEmit = now;
        this.emitPlaybackState();
      }
    },

    // Reflects the viewerUiStore layer visibility onto every marker's mesh. Called on
    // store changes (toggles) and after marker sync (load) so newly added markers also
    // respect the current layer state.
    applyLayerVisibility() {
      const layers = this.viewerUi.layers;

      // When isolating to a zone, anything (marker) whose position falls outside the zone box is
      // hidden on top of the layer toggles — the zone itself always stays visible.
      const isolateObb = this.isolatedZone ? this.createAreaObb(this.isolatedZone) : null;
      const wp = new THREE.Vector3();

      this.markers.forEach((marker) => {
        let visible;
        if (marker instanceof AnomalyMarker) {
          visible = layers.anomalies;
        } else if (marker instanceof HotSpotMarker) {
          visible = layers.hotspots;
        } else if (marker instanceof InspectionAreaMarker) {
          visible = layers.zones;
        } else if (marker instanceof CameraHelperMarker) {
          visible = layers.cameras;
        } else {
          return;
        }

        if (isolateObb && visible && marker !== this.isolatedZone) {
          marker.meshGroup.getWorldPosition(wp);
          visible = isolateObb.containsPoint(wp);
        }

        marker.meshGroup.visible = visible;
      });

      // Walking-path polylines hang off the scan groups, not markers, so they're toggled here
      // alongside the marker layers (driven by the same viewerUiStore subscription).
      if (this.scanGroups) {
        for (const entry of this.scanGroups.values()) {
          if (entry.cameraPath) entry.cameraPath.visible = layers.walkingPath;
        }
      }

      // Turning the Cameras layer off drops any shown projection frustum.
      if (!layers.cameras && this._selectedCamera) this.clearSelectedCamera();
    },

    // Camera-position dots scale with the model so they read at any scale (markers can load either
    // before or after the model is measured — this is called from both paths).
    sizeCameraApexes() {
      if (!(this.modelRadius > 0) || !this.markers) return;
      const s = this.modelRadius * 0.011;
      for (const m of this.markers) {
        if (m instanceof CameraHelperMarker && m.apex) m.apex.scale.setScalar(s);
      }
    },

    // Double-clicking a camera dot reveals THAT camera's projection frustum — enlarged out from the
    // apex and recoloured to the accent — so you can see where it was looking. Double-clicking the
    // same camera again (or anything else / empty space) clears it. Only one is shown at a time.
    selectCameraHelper(marker) {
      if (!marker) return;
      if (this._selectedCamera === marker) { this.clearSelectedCamera(); return; }
      this.clearSelectedCamera();

      const helper = marker.meshGroup;
      const cam = helper?.camera;
      if (!helper?.material || !cam) return;

      // The helper's transform is frozen to the camera's world matrix (matrixAutoUpdate=false), so
      // scaling it does nothing — enlarge the projection by pushing the camera's far plane out and
      // rebuilding the frustum geometry.
      cam.far = Math.max((marker.originalFar || cam.far) * 4, (this.modelRadius || 1) * 0.5);
      cam.updateProjectionMatrix();
      helper.update();

      helper.material.visible = true;          // reveal the frustum wireframe
      helper.material.vertexColors = false;    // uniform accent instead of the multi-colour default
      helper.material.color.set(0x64ffda);
      helper.material.needsUpdate = true;
      if (marker.apex?.material) marker.apex.material.color.set(0x64ffda);
      this._selectedCamera = marker;
    },

    clearSelectedCamera() {
      const marker = this._selectedCamera;
      if (!marker) return;
      const helper = marker.meshGroup;
      const cam = helper?.camera;
      if (cam && marker.originalFar != null) {   // restore the real far so image ranking is unaffected
        cam.far = marker.originalFar;
        cam.updateProjectionMatrix();
        helper.update();
      }
      if (helper?.material) {
        helper.material.visible = false;
        helper.material.vertexColors = true;
        helper.material.needsUpdate = true;
      }
      if (marker.apex?.material) marker.apex.material.color.set(0xa78bfa);
      this._selectedCamera = null;
    },

    // Selecting / deselecting / isolating inspection zones (driven by clicking the wireframe edge
    // or name label, and the JIT sidebar's Isolate toggle).
    selectInspectionArea(zone) {
      if (!zone) return;

      // Selecting a zone drops any anomaly/hotspot selection.
      this.deselectMarkerGroup();

      if (this.selectedInspectionArea && this.selectedInspectionArea !== zone) {
        this.selectedInspectionArea.setSelected?.(false);
      }
      // A fresh selection always starts un-isolated (the JIT toggle resets per selection).
      if (this.isolatedZone) this.setZoneIsolation(false);

      this.selectedInspectionArea = zone;
      zone.setSelected?.(true);

      // Frame the whole zone box (not just dive to its anchor point).
      this.frameInspectionZone(zone);

      this.$emit('insideInspectionArea', zone);
    },

    clearInspectionAreaSelection() {
      if (this.isolatedZone) this.setZoneIsolation(false);
      if (this.selectedInspectionArea) {
        this.selectedInspectionArea.setSelected?.(false);
        this.selectedInspectionArea = null;
        this.$emit('insideInspectionArea', null);
      }
    },

    // Isolate the point cloud + markers to the selected zone (or clear when enabled = false). The
    // point cloud is clipped in the cull shader (a box uniform); markers are filtered by position
    // via applyLayerVisibility.
    setZoneIsolation(enabled) {
      this.exitBubbleForAction();
      let zone = null;
      if (enabled && this.selectedInspectionArea) {
        // Re-resolve the live marker by id: the stored selection reference can point at a
        // pre-transform instance after the markers array is rebuilt on a transform-save, which
        // would isolate the zone's OLD position. Use the current in-scene instance instead.
        zone = this.markers.find((m) => m.id === this.selectedInspectionArea.id) || this.selectedInspectionArea;
        this.selectedInspectionArea = zone;
      }
      this.isolatedZone = zone;

      if (this.scanGroups) {
        let inv = null;
        if (zone) {
          zone.meshGroup.updateMatrixWorld(true);
          inv = new THREE.Matrix4().copy(zone.meshGroup.matrixWorld).invert();
        }
        for (const entry of this.scanGroups.values()) {
          if (entry.pointCloud?.material) setMaterialClipBox(entry.pointCloud.material, inv);
        }
      }

      this.applyLayerVisibility();
    },

    // Slice / cut-away tool: cull points outside an axis-aligned WORLD-space box so you can, e.g.,
    // drop the max on the up-axis to "take the roof off" and see inside. `box` is
    // { min:{x,y,z}, max:{x,y,z} } in world coords; pass through clearSliceBox to turn it off.
    // Independent of zone isolation (separate shader uniform), so both can be active together.
    setSliceBox(box) {
      this.exitBubbleForAction();
      if (!this.scanGroups || !box) return;
      for (const entry of this.scanGroups.values()) {
        if (entry.pointCloud?.material) setMaterialSliceBox(entry.pointCloud.material, box);
      }
    },
    clearSliceBox() {
      if (!this.scanGroups) return;
      for (const entry of this.scanGroups.values()) {
        if (entry.pointCloud?.material) setMaterialSliceBox(entry.pointCloud.material, null);
      }
    },

    // Schedule the one-time perimeter capture on idle so it never competes with the first render.
    scheduleCapturePerimeters() {
      const run = () => this.captureMissingPerimeters();
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(run, { timeout: 4000 });
      } else {
        setTimeout(run, 1500);
      }
    },
    // For each loaded scan with no stored perimeter, compute it from the LOCAL geometry and persist it
    // (the anomaly-management minimap reads it back and applies the version transform). Best-effort:
    // any failure is swallowed so it never affects viewing; a race with another viewer just re-writes
    // equivalent data. Geometry positions are scan-local (the transform lives on the group).
    async captureMissingPerimeters() {
      try {
        if (!this.assetId || !this.scanGroups || this.scanGroups.size === 0) return;

        const scans = await ModelVersionService.getScansByAssetId(this.assetId).catch(() => []);
        const alreadyCaptured = new Set((scans || []).filter(s => s.perimeter).map(s => s.id));

        for (const [scanId, entry] of this.scanGroups) {
          if (alreadyCaptured.has(scanId)) continue;
          const posAttr = entry?.pointCloud?.geometry?.attributes?.position;
          if (!posAttr?.count) continue;

          const perimeter = computeScanPerimeter(posAttr.array, posAttr.count);
          if (!perimeter) continue;

          await ModelVersionService.updateScanPerimeter(this.assetId, scanId, JSON.stringify(perimeter))
            .catch((e) => {
              if (process.env.NODE_ENV === 'development') console.warn('DEV: perimeter capture PUT failed', scanId, e);
            });
        }
      } catch (e) {
        if (process.env.NODE_ENV === 'development') console.warn('DEV: perimeter capture failed', e);
      }
    },

    // Recursively dispose a three.js object's GPU resources (geometries, materials, and any
    // textures the materials reference). three.js does not free these on garbage collection, so
    // without this every loaded cloud / marker / label leaks GPU memory for the life of the tab.
    disposeObject3D(obj) {
      if (!obj) return;
      obj.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        const materials = Array.isArray(child.material)
          ? child.material
          : (child.material ? [child.material] : []);
        for (const material of materials) {
          for (const key in material) {
            const value = material[key];
            if (value && value.isTexture) value.dispose();
          }
          material.dispose();
        }
      });
    },

    // Keep every zone's fat-line material in sync with the viewport so the lines render at the
    // right pixel width and raycast accurately. Called on load + resize.
    sizeAllZoneLines() {
      const w = this.$refs.canvasContainer?.clientWidth || (typeof window !== 'undefined' ? window.innerWidth : 1);
      const h = this.$refs.canvasContainer?.clientHeight || (typeof window !== 'undefined' ? window.innerHeight : 1);
      this.markers?.forEach((m) => {
        if (m instanceof InspectionAreaMarker && m.lineSegments?.material?.resolution) {
          m.lineSegments.material.resolution.set(w, h);
        }
      });
    },

    setTransformToScale() {
      this.transformControls.setMode('scale');
    },

    setTransformToMove() {
      this.transformControls.setMode('translate');
    },

    setTransformToRotate() {
      this.transformControls.setMode('rotate');
    },

    deselectMarkerGroup() {
      if (this.selectedMarker instanceof AnomalyMarker) {
        // Restore the marker to its own (category) colour.
        this.setMarkerGroupColor(this.selectedMarker, this.selectedMarker.defaultColor);
        this.selectedMarker = null;
      }
    },

    // Set every mesh/line material colour in a marker's group. Accepts a hex string
    // (e.g. '#FF5E3A', the category colour) or a numeric hex (e.g. 0x66C7B4).
    setMarkerGroupColor(marker, color) {
      if (!marker || !marker.meshGroup || color == null) return;
      marker.meshGroup.traverse((child) => {
        if ((child.isMesh || child.isLineSegments) && child.material) {
          child.material.color.set(color);
        }
      });
    },

    onDoubleClick(event) {
      const savedPoint = this.highlightedPoint;
      const selectedPointIndex = this.highlightedPointIndex;
      const rect = this.$refs.canvasContainer.getBoundingClientRect();
      this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;


      this.raycaster.precision = 0.00001;
      this.camera.updateMatrixWorld();
      this.raycaster.setFromCamera(this.mouse, this.camera);

      // Filter for Mesh objects only
      const meshObjects = [];
      this.markers.forEach((marker) => {
        if (marker instanceof AnomalyMarker || marker instanceof HotSpotMarker) {
          const mesh = marker.meshGroup.children.find((child) => child.isMesh);
          if (mesh && marker.meshGroup.visible) {
            meshObjects.push(mesh);
          }
        }
      });

      const intersects = this.raycaster.intersectObjects(meshObjects, true);

      if (intersects.length > 0) {
        const selectedMesh = intersects[0].object;

        // Find the parent group
        let markerGroup = this.markers.find(m => m.id == selectedMesh.userData.markerId);

        if (markerGroup) {
          // Picking an anomaly/hotspot in the scene drops any inspection-zone selection (and its
          // isolation). Navigating from the JIT list goes through locateMarkerById, which does NOT
          // clear the zone, so the zone's marker list stays usable.
          this.clearInspectionAreaSelection();
          this.clearSelectedCamera();
          this.onMarkerSelected(markerGroup);
        }

        return;
      }

      // Camera dots (Cameras layer on): double-click one to reveal its projection frustum.
      if (this.viewerUi.layers.cameras) {
        const apexes = [];
        this.markers.forEach((m) => {
          if (m instanceof CameraHelperMarker && m.meshGroup.visible && m.apex) apexes.push(m.apex);
        });
        const camHits = apexes.length ? this.raycaster.intersectObjects(apexes, false) : [];
        if (camHits.length > 0) {
          const camMarker = this.markers.find(m => m.id === camHits[0].object.userData.markerId);
          if (camMarker) { this.selectCameraHelper(camMarker); return; }
        }
      }

      // No anomaly/hotspot hit — try the inspection-zone edges + name labels (only visible ones).
      const zoneTargets = [];
      this.markers.forEach((marker) => {
        if (marker instanceof InspectionAreaMarker && marker.meshGroup.visible) {
          if (marker.lineSegments) zoneTargets.push(marker.lineSegments);
          if (marker.labelSprite) zoneTargets.push(marker.labelSprite);
        }
      });

      if (zoneTargets.length > 0) {
        const zoneHits = this.raycaster.intersectObjects(zoneTargets, false);
        if (zoneHits.length > 0) {
          const zoneId = zoneHits[0].object.userData.markerId;
          const zone = this.markers.find(m => m.id === zoneId);
          if (zone) {
            this.clearSelectedCamera();
            this.selectInspectionArea(zone);
            return;
          }
        }
      }

      // Double-clicked off any marker AND off any zone → exit zone context: drop the zone
      // selection, remove its isolation, and bring the full cloud + all markers back.
      this.clearInspectionAreaSelection();
      this.clearSelectedCamera();

      // If no existing cube is selected, proceed with intersecting the point cloud or model.
      // Resolve the scan + point freshly at the click coords so the new annotation is associated
      // with the exact scan that was clicked (not whatever was last hovered).
      if (this.pointCloud || this.objModel) {
        const hit = this.findClosestScanPoint(this.raycaster.ray);
        if (hit) {
          this.highlightedPoint.copy(hit.worldPoint);
          this.highlightedScanId = hit.scanId;
          this.highlightedPointIndex = hit.index;
          this.selectPoint(hit.worldPoint.clone());
          this.selectedPointIndex = hit.index;
        } else {
          this.selectPoint(savedPoint);
          this.selectedPointIndex = selectedPointIndex;
        }
      }
    },

    onMarkerSelected(markerGroup) {
      // Restore the previously selected anomaly to its own (category) colour before we
      // highlight the new selection.
      if (this.selectedMarker && this.selectedMarker instanceof AnomalyMarker) {
        this.setMarkerGroupColor(this.selectedMarker, this.selectedMarker.defaultColor);
      }

      // Set the new selected marker
      this.selectedMarker = markerGroup;

      // Highlight the new selection.
      if (this.selectedMarker) {
        this.setMarkerGroupColor(this.selectedMarker, 0x66C7B4);
      }

      // World position of the selected marker — used for both image ranking (cameras + the BVH
      // occlusion test all live in world space) and for framing the camera below.
      const objectWorldPosition = new THREE.Vector3();
      this.selectedMarker.meshGroup.getWorldPosition(objectWorldPosition);

      const filenames = this.getImageFilenamesForPoint(objectWorldPosition);

      this.$emit('markerSelected', { marker: this.selectedMarker, imageFilenames: filenames });

      // Change the camera direction to focus on the selected anomaly marker
      this.changeCameraDirection(objectWorldPosition);
    },

    // Move the camera so the ENTIRE selected zone box is in view, rather than zooming to its
    // anchor point. Measures the wireframe's world-space bounds (so a rotated / scaled / resized
    // zone is always fully contained), then backs the camera off along its current view direction
    // far enough for the box's bounding sphere to fit whichever FOV axis is tighter, plus padding.
    frameInspectionZone(zone) {
      if (!zone) return;

      const target = zone.lineSegments || zone.meshGroup;
      target.updateMatrixWorld(true);

      const box = new THREE.Box3().setFromObject(target);
      if (box.isEmpty()) {
        // No measurable bounds — fall back to looking at the anchor.
        const anchor = new THREE.Vector3();
        zone.meshGroup.getWorldPosition(anchor);
        this.changeCameraDirection(anchor);
        return;
      }

      const sphere = box.getBoundingSphere(new THREE.Sphere());
      const center = sphere.center;
      const radius = Math.max(sphere.radius, 0.001);

      // camera.fov is the VERTICAL fov (degrees); derive the horizontal half-angle from aspect and
      // fit to the smaller of the two so the box clears both edges of the viewport.
      const vFovHalf = THREE.MathUtils.degToRad(this.camera.fov) / 2;
      const hFovHalf = Math.atan(Math.tan(vFovHalf) * this.camera.aspect);
      const fitHalf = Math.min(vFovHalf, hFovHalf);
      const PADDING = 1.25; // ~25% breathing room around the box

      let distance = (radius / Math.sin(fitHalf)) * PADDING;
      // Keep within the orbit dolly limits so the box can't push the camera past them.
      distance = THREE.MathUtils.clamp(distance, this.orbitControls.minDistance, this.orbitControls.maxDistance);

      const direction = new THREE.Vector3();
      this.camera.getWorldDirection(direction);

      this.camera.position.copy(center).sub(direction.multiplyScalar(distance));
      this.orbitControls.target.copy(center);
      this.orbitControls.update();
    },

    changeCameraDirection(intersectionPoint, zoomFactor = 0.2) {
      if (!intersectionPoint || !(intersectionPoint instanceof THREE.Vector3)) {
        console.error("Invalid intersectionPoint:", intersectionPoint);
        return;
      }

      // Get current camera direction
      const direction = new THREE.Vector3();
      this.camera.getWorldDirection(direction);

      // Zoom to a distance proportional to the model so double-clicking a point/anomaly lands a
      // consistent close-up at any scale. Falls back to the old relative zoom before the model
      // bounds are known (fitClipPlanesToModel sets modelRadius after load).
      const distance = this.camera.position.distanceTo(this.orbitControls.target);
      const zoomedDistance = this.modelRadius > 0 ? this.modelRadius * 0.08 : distance * zoomFactor;

      // Calculate the new camera position (clone direction to avoid modifying the original vector)
      const newCameraPosition = intersectionPoint.clone().sub(direction.clone().multiplyScalar(zoomedDistance));

      // Set the camera position and update controls
      this.camera.position.copy(newCameraPosition);
      this.orbitControls.target.copy(intersectionPoint);

      // Mobile portrait: the bottom sheet covers the lower half, so bias the framing upward — the
      // located point lands in the top half of the screen instead of dead-centre behind the sheet.
      const el = this.renderer?.domElement;
      if (el && el.clientHeight > el.clientWidth) {
        const vFovHalf = THREE.MathUtils.degToRad(this.camera.fov) / 2;
        const visibleH = 2 * zoomedDistance * Math.tan(vFovHalf);
        const shift = new THREE.Vector3(0, 1, 0).applyQuaternion(this.camera.quaternion)
          .multiplyScalar(-visibleH * 0.25);
        this.camera.position.add(shift);
        this.orbitControls.target.add(shift);
      }

      this.orbitControls.update();
    },

    // Image filenames whose camera actually has a good, unobstructed view of `worldPoint`, best
    // view first. Thin wrapper over rankImagesForPoint that preserves the existing emit contract
    // (an array of filename strings) while now being ranked + occlusion-filtered.
    getImageFilenamesForPoint(worldPoint) {
      return this.rankImagesForPoint(worldPoint).map((r) => r.imageName);
    },

    // Rank the camera-helper images by how well each one sees `worldPoint` (a THREE.Vector3 in
    // world space). Returns [{ imageName, distance, centrality, score }] sorted best-first.
    //
    // Three filters, cheapest first so this stays fast even with ~1500 cameras (runs once per
    // selection, never per frame):
    //   1. In-frame  — project the point with each camera; keep only cameras whose view volume
    //                  (incl. near/far planes → a natural max view distance) contains it.
    //   2. Distance + centrality — closer cameras, and points nearer the image centre (less
    //                  grazing / lens distortion), rank higher. This is the fix for "everything
    //                  in the frustum counts": a camera 30 m away no longer ties one 2 m away.
    //   3. Occlusion — for the small in-frame set only, raycast camera→point through the dense
    //                  cloud's BVH and drop cameras whose line of sight is blocked (a wall in
    //                  between). Skipped if no BVH is ready yet (graceful: shows un-occluded set).
    rankImagesForPoint(worldPoint, { checkOcclusion = true } = {}) {
      if (!worldPoint) return [];

      const ndc = new THREE.Vector3();
      const camWorldPos = new THREE.Vector3();
      const candidates = [];

      for (const marker of this.markers) {
        if (!(marker instanceof CameraHelperMarker)) continue;
        const camera = marker.meshGroup?.camera;
        if (!camera || !marker.imageName) continue;

        // Project into this camera's clip space. Outside [-1,1] on any axis → not in frame
        // (the z test also enforces the near/far planes, i.e. an effective max view distance).
        ndc.copy(worldPoint).project(camera);
        if (ndc.x < -1 || ndc.x > 1 || ndc.y < -1 || ndc.y > 1 || ndc.z < -1 || ndc.z > 1) continue;

        camera.getWorldPosition(camWorldPos);
        const distance = camWorldPos.distanceTo(worldPoint);
        // 0 = dead centre of frame, ~1.41 = a corner. Penalise edge-of-frame mildly so distance
        // stays the dominant factor (the user's primary concern).
        const centrality = Math.hypot(ndc.x, ndc.y);
        const score = distance * (1 + 0.5 * centrality); // lower is better

        candidates.push({ imageName: marker.imageName, distance, centrality, score, camWorldPos: camWorldPos.clone() });
      }

      candidates.sort((a, b) => a.score - b.score);

      // Occlusion is the only non-trivial cost — bound it. In-frame candidates for a single point
      // are already few, but cap to a safety ceiling after the cheap sort just in case.
      const OCCLUSION_MAX = 80;
      let result = candidates;
      if (checkOcclusion) {
        const toTest = candidates.slice(0, OCCLUSION_MAX);
        result = toTest.filter((c) => !this.isLineOfSightBlocked(c.camWorldPos, worldPoint))
          .concat(candidates.slice(OCCLUSION_MAX)); // un-tested overflow kept (rare), still ranked
      }

      // Drop the scratch world-position before returning.
      return result.map(({ imageName, distance, centrality, score }) => ({ imageName, distance, centrality, score }));
    },

    // True if a cloud point lies on the line of sight between the camera and the target, i.e. the
    // camera's view of the target is blocked. Tested per scan against that scan's BVH (degenerate
    // point-cloud mesh), reusing the distance-to-ray shapecast pattern from findClosestScanPoint.
    isLineOfSightBlocked(cameraWorldPos, targetWorldPos) {
      if (!this.scanGroups) return false;

      const worldDir = new THREE.Vector3().subVectors(targetWorldPos, cameraWorldPos);
      const worldDist = worldDir.length();
      if (worldDist === 0) return false;
      worldDir.normalize();
      const worldRay = new THREE.Ray(cameraWorldPos.clone(), worldDir);

      // How close a cloud point must be to the ray to count as an occluder. Point clouds are
      // sparse, so a zero-width ray would thread between points and never report a block; use a
      // small angular radius (≈1.2% of the camera distance, floored) so a real wall registers.
      const losRadius = Math.max(0.03, worldDist * 0.012);
      // Ignore hits in the last 10% before the target — that's the surface the anomaly sits ON,
      // not an occluder in front of it.
      const blockBefore = worldDist * 0.9;
      // ...and skip points hugging the camera itself.
      const nearSkip = Math.min(0.1, worldDist * 0.02);

      const scale = new THREE.Vector3();
      const q = new THREE.Quaternion();
      const t = new THREE.Vector3();

      for (const entry of this.scanGroups.values()) {
        const bvhMesh = entry.bvhMesh;
        if (!bvhMesh || !bvhMesh.geometry.boundsTree) continue; // BVH not built yet → can't test
        if (entry.group.visible === false) continue;

        bvhMesh.updateMatrixWorld();
        const inverse = new THREE.Matrix4().copy(bvhMesh.matrixWorld).invert();
        const localRay = worldRay.clone().applyMatrix4(inverse);

        // World→local distances scale by the scan's (assumed ~uniform) scale.
        bvhMesh.matrixWorld.decompose(t, q, scale);
        const avgScale = (Math.abs(scale.x) + Math.abs(scale.y) + Math.abs(scale.z)) / 3 || 1;
        const localRadiusSq = (losRadius / avgScale) ** 2;
        const localBlockBefore = blockBefore / avgScale;
        const localNearSkip = nearSkip / avgScale;

        let blocked = false;
        bvhMesh.geometry.boundsTree.shapecast({
          intersectsBounds: (box) => {
            box.expandByScalar(losRadius / avgScale);
            return localRay.intersectsBox(box) ? INTERSECTED : NOT_INTERSECTED;
          },
          intersectsTriangle: (triangle) => {
            const dSq = localRay.distanceSqToPoint(triangle.a);
            if (dSq < localRadiusSq) {
              const along = localRay.origin.distanceTo(triangle.a);
              if (along > localNearSkip && along < localBlockBefore) {
                blocked = true;
                return true; // stop the shapecast early
              }
            }
            return false;
          },
        });

        if (blocked) return true;
      }

      return false;
    },

    selectPoint(intersectionPoint) {
      this.changeCameraDirection(intersectionPoint);
      this.selectedPoint = intersectionPoint;

      // intersectionPoint is world space; convert to the hit scan's local space for storage and
      // for the (scan-local) inspection-area containment test.
      const scanId = this.highlightedScanId;
      const localPoint = this.toScanLocal(scanId, intersectionPoint);

      if (process.env.NODE_ENV === 'development') {
        const isPrimary = scanId === this.modelVersion?.associatedScan;
        console.log('[ModelViewer] point picked on scan', scanId, isPrimary ? '(PRIMARY)' : '(supplement)',
          'world', intersectionPoint, 'local', localPoint);
      }

      const filenames = this.getImageFilenamesForPoint(intersectionPoint);
      const inspectionAreaId = markerGeometry.findInspectionAreaForPoint(localPoint, this.markers, scanId);

      this.$emit('onPointSelected', {
        point: intersectionPoint.clone(),
        localPoint: localPoint.clone(),
        scanId,
        imageFilenames: filenames,
        inspectionAreaId,
      });
    },

    transformMarker(marker) {
      this.exitBubbleForAction();
      // Locate the marker group with the matching anomalyId
      const selectedGroup = this.markers.find((group) =>
        group.id === marker.id
      );

      if (!selectedGroup) {
        console.warn(`Marker with not found.`);
        return;
      }

      this.selectedMarker = selectedGroup;
      // clone() drops the markRaw flag, so re-mark it before it lands in reactive data.
      this.selectedMarkerGroupTransformBackup = markRaw(this.selectedMarker.meshGroup.clone());

      if (marker instanceof HotSpotMarker) {
        this.updateHotSpotAnimation(marker, true);
      }

      // Attach the group to transform controls
      this.transformControls.attach(selectedGroup.meshGroup);
      this.isTransformActive = true;

      this.$emit('isTransformActive', this.isTransformActive);
    },

    updateHotSpotAnimation(marker, stop = false) {
      // Cancel any previous hotspot loop before starting a new one — each transform/cancel/save
      // call used to spawn a fresh, never-cancelled requestAnimationFrame loop, so they piled up
      // and ran concurrently for the life of the page. Now at most one runs, and it's torn down
      // in beforeUnmount.
      if (this.hotSpotAnimationFrameId) {
        cancelAnimationFrame(this.hotSpotAnimationFrameId);
        this.hotSpotAnimationFrameId = null;
      }

      const animate = () => {
        this.hotSpotAnimationFrameId = requestAnimationFrame(animate);
        this.orbitControls.update();
        if (stop) {
          marker.stopAnimation();
        } else {
          marker.animate();
        }
        this.renderer.render(this.scene, this.camera);
      };

      animate();
    },

    cancelTransformMarker() {
      // Replace the markerGroup object by id with the backup and set to null.
      const index = this.markers.findIndex((group) => group.id === this.selectedMarker.id);
      if (index !== -1) {
        // Replace the marker group with the backup entity
        const group = this.selectedMarker.meshGroup;
        if (group) {
          group.position.copy(this.selectedMarkerGroupTransformBackup.position);
          group.rotation.copy(this.selectedMarkerGroupTransformBackup.rotation);
          group.scale.copy(this.selectedMarkerGroupTransformBackup.scale);
        }
      }

      if (this.selectedMarker instanceof HotSpotMarker) {
        this.updateHotSpotAnimation(this.selectedMarker);
      }

      this.detachTransformFromMarker();
    },

    detachTransformFromMarker() {
      this.selectedMarkerGroupTransformBackup = null;
      this.transformControls.detach(this.selectedMarker.meshGroup);
      this.isTransformActive = false;
    },

    async saveMarkerTransform() {
      let updatedMarker = this.selectedMarker;

      updatedMarker.transform.position = this.transformControls.object.position;
      updatedMarker.transform.scale = this.transformControls.object.scale;

      updatedMarker.transform.rotation.x = this.transformControls.object.rotation._x;
      updatedMarker.transform.rotation.y = this.transformControls.object.rotation._y;
      updatedMarker.transform.rotation.z = this.transformControls.object.rotation._z;
      updatedMarker.transform.rotation.isEuler = this.transformControls.object.rotation.isEuler;
      updatedMarker.transform.rotation.order = this.transformControls.object.rotation._order;

      let areaConflict = false;
      let markerIds = [];
      let hotSpotIds = [];
      if (updatedMarker instanceof InspectionAreaMarker) {
        areaConflict = this.checkInspectionAreaHasConflicts(updatedMarker);
        if (areaConflict) {
          this.$emit('onAreaConflict');
          return false;
        }

        ({ markerIds, hotSpotIds } = this.findEncompassingMarkers(updatedMarker));

        // A resize changed the group scale — keep the name label a constant on-screen size.
        if (updatedMarker.labelSprite) {
          updatedMarker.applyLabelScale(updatedMarker.labelSprite, updatedMarker.meshGroup.scale);
        }
        // If this zone is currently isolated, its clip box moved/resized — refresh it (compare by
        // id so a rebuilt instance still matches; setZoneIsolation re-resolves the live marker).
        if (this.isolatedZone && this.isolatedZone.id === updatedMarker.id) this.setZoneIsolation(true);
      }

      if (updatedMarker instanceof AnomalyMarker) {
        // find the inspectionAreaId (scan-local position, restricted to the marker's scan)
        const inspectionId = this.isPointInsideInspectionArea(updatedMarker.transform.position, updatedMarker.scanId);
        updatedMarker.inspectionAreaId = inspectionId;
      }

      if (updatedMarker instanceof HotSpotMarker) {
        const inspectionId = this.isPointInsideInspectionArea(updatedMarker.transform.position, updatedMarker.scanId);
        updatedMarker.inspectionAreaId = inspectionId;

        const anomalyIds = this.findMarkersInsideHotSpot(updatedMarker);
        updatedMarker.anomalyIds = anomalyIds;

        this.updateHotSpotAnimation(this.selectedMarker);
      }

      this.detachTransformFromMarker();
      this.$emit('markerModified', updatedMarker, markerIds, hotSpotIds);
      return true;
    },


    checkInspectionAreaHasConflicts(marker) {
      return markerGeometry.inspectionAreaHasConflicts(marker, this.markers, marker.scanId);
    },

    createAreaObb(area) {
      return markerGeometry.createAreaObb(area);
    },

    findMarkersInsideHotSpot(newHotSpot) {
      return markerGeometry.findMarkersInsideHotSpot(newHotSpot, this.markers, newHotSpot.scanId);
    },

    findEncompassingMarkers(marker) {
      return markerGeometry.findEncompassingMarkers(marker, this.markers, marker.scanId);
    },

    isPointInsideInspectionArea(point, scanId) {
      return markerGeometry.findInspectionAreaForPoint(point, this.markers, scanId);
    },

    // FIX EVERYTHING UNDER THIS LINE //











    onClick() {
      // Single click deselects an anomaly only. Inspection-zone selection/isolation is deliberately
      // NOT cleared here — a small camera nudge registers as a click, and we don't want that to drop
      // the zone + its isolation. Zones clear only on a double-click off the zone (see onDoubleClick).
      if (this.selectedMarker && !this.hasMoved && !this.isTransformActive && !this.markerdetailswindowopen) {
        this.deselectMarkerGroup();
        return;
      }
    },

    locateMarkerById(markerId) {
      this.exitBubbleForAction();
      this.markerdetailswindowopen = true;
      // Get markerGroup using anomalyId
      const markerGroup = this.findMarkerById(markerId);

      // Pass the entire group to the selection handler
      this.onMarkerSelected(markerGroup);
    },

    navigateToHotSpot(hotSpotId) {
      this.exitBubbleForAction();
      const group = this.findMarkerById(hotSpotId);
      this.onMarkerSelected(group);
    },

    findMarkerById(markerId) {
      for (const marker of this.markers) {
        // Iterate through each child in the group
        if (marker.id === markerId) {
          return marker; // Return the parent group if a match is found
        }
      }
      return null; // Return null if no matching group is found
    },
  },
};
</script>

<style scoped>
.canvas-container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  border: 1px solid #2b3b43;
}

/* ----- Model loading overlay ----- */
.model-loading {
  position: absolute;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: radial-gradient(120% 90% at 50% 45%, rgba(26, 38, 45, 0.7) 0%, rgba(16, 22, 26, 0.82) 100%);
  backdrop-filter: blur(2px);
}

.ml-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: #fff;
  perspective: 320px;
}

.ml-percent {
  font-size: 1.1rem;
  font-weight: 700;
  color: #d6f8f1;
  font-variant-numeric: tabular-nums;
}

.ml-title {
  font-size: 1.05rem;
  font-weight: 700;
  letter-spacing: 0.01em;
}

.ml-sub {
  font-size: 0.85rem;
  color: #8aa0aa;
  margin-top: -8px;
}

/* Rotating 3D cube for the "preparing" phase */
.ml-cube {
  position: relative;
  width: 58px;
  height: 58px;
  transform-style: preserve-3d;
  animation: ml-cube-spin 2.6s infinite ease-in-out;
  margin-bottom: 8px;
}

.ml-face {
  position: absolute;
  width: 58px;
  height: 58px;
  background: rgba(102, 199, 180, 0.16);
  border: 1.5px solid rgba(102, 199, 180, 0.85);
  box-shadow: inset 0 0 18px rgba(102, 199, 180, 0.25);
}

.ml-front  { transform: translateZ(29px); }
.ml-back   { transform: rotateY(180deg) translateZ(29px); }
.ml-right  { transform: rotateY(90deg) translateZ(29px); }
.ml-left   { transform: rotateY(-90deg) translateZ(29px); }
.ml-top    { transform: rotateX(90deg) translateZ(29px); }
.ml-bottom { transform: rotateX(-90deg) translateZ(29px); }

@keyframes ml-cube-spin {
  0%   { transform: rotateX(-24deg) rotateY(0deg); }
  100% { transform: rotateX(-24deg) rotateY(360deg); }
}
</style>
